import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import ModuleKanbanMenu from '../components/ModuleKanbanMenu';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, BarChart, Bar, XAxis, YAxis, Legend, CartesianGrid } from 'recharts';
import {
  Plus, Loader2, ChevronLeft, ChevronDown, ChevronRight, X, Trash2, Edit, RefreshCw,
  CheckCircle2, XCircle, AlertCircle, Clock, Upload, Download, FileText,
  Users, Calendar, LogOut, ShieldCheck, MessageSquare, Image as ImageIcon, ListChecks,
  Save, Check, Bell, Send, TrendingUp, Target, Flag,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import CertSignaturePanel from '../components/CertSignaturePanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_COLORS = {
  pending: { cls: 'bg-gray-100 text-gray-700', label: 'Pendiente', icon: Clock },
  in_progress: { cls: 'bg-blue-100 text-blue-700', label: 'En curso', icon: RefreshCw },
  passed: { cls: 'bg-green-100 text-green-700', label: 'Aprobado', icon: CheckCircle2 },
  failed: { cls: 'bg-red-100 text-red-700', label: 'Fallido', icon: XCircle },
  blocked: { cls: 'bg-amber-100 text-amber-700', label: 'Bloqueado', icon: AlertCircle },
};
const PRIORITY_LABEL = { high: 'Alta', medium: 'Media', low: 'Baja' };

// Color palette for milestones (background tint + border + text)
// Used in EstructuraTab to color backlog cards by parent Hito, and in MatrizTab kanban cards as a thin band.
const MILESTONE_PALETTE = [
  { bg: 'bg-blue-50', border: 'border-blue-300', strip: 'bg-blue-500', label: 'text-blue-700' },
  { bg: 'bg-emerald-50', border: 'border-emerald-300', strip: 'bg-emerald-500', label: 'text-emerald-700' },
  { bg: 'bg-amber-50', border: 'border-amber-300', strip: 'bg-amber-500', label: 'text-amber-700' },
  { bg: 'bg-purple-50', border: 'border-purple-300', strip: 'bg-purple-500', label: 'text-purple-700' },
  { bg: 'bg-pink-50', border: 'border-pink-300', strip: 'bg-pink-500', label: 'text-pink-700' },
  { bg: 'bg-cyan-50', border: 'border-cyan-300', strip: 'bg-cyan-500', label: 'text-cyan-700' },
  { bg: 'bg-orange-50', border: 'border-orange-300', strip: 'bg-orange-500', label: 'text-orange-700' },
  { bg: 'bg-indigo-50', border: 'border-indigo-300', strip: 'bg-indigo-500', label: 'text-indigo-700' },
];
const milestonePalette = (index = 0) => MILESTONE_PALETTE[((index % MILESTONE_PALETTE.length) + MILESTONE_PALETTE.length) % MILESTONE_PALETTE.length];

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const parseError = (err, fallback = 'Error') => {
  const detail = err?.response?.data?.detail;
  if (Array.isArray(detail)) {
    return detail.map(d => (d?.msg ? `${(d.loc || []).slice(-1)[0] || ''}: ${d.msg}` : String(d))).join(' | ');
  }
  if (typeof detail === 'string') return detail;
  return err?.message || fallback;
};

const ALLOWED_MIMES = {
  'image/png': 5, 'image/jpeg': 5, 'image/jpg': 5, 'image/webp': 5, 'image/gif': 5,
  'application/pdf': 10,
};

const readFileAsDataUrl = (file) => new Promise((res, rej) => {
  const mime = file.type || '';
  const maxMB = ALLOWED_MIMES[mime];
  if (!maxMB) return rej(new Error(`${file.name}: tipo no permitido (${mime || 'desconocido'}). Solo imagenes y PDF.`));
  if (file.size > maxMB * 1024 * 1024) return rej(new Error(`${file.name} excede ${maxMB}MB`));
  const r = new FileReader();
  r.onload = () => res(r.result);
  r.onerror = rej;
  r.readAsDataURL(file);
});

const isPdfEvidence = (ev) => {
  if (ev?.content_type === 'application/pdf' || ev?.resource_type === 'raw') return true;
  if (ev?.secure_url && /\.pdf($|\?)/i.test(ev.secure_url)) return true;
  if (ev?.data_url && ev.data_url.startsWith('data:application/pdf')) return true;
  if (ev?.filename && /\.pdf$/i.test(ev.filename)) return true;
  return false;
};
const evidenceUrl = (ev) => ev?.secure_url || ev?.data_url || '';

// ============================================================================
// ROOT
// ============================================================================
const QAPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showNewPlanModal, setShowNewPlanModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);

  const handleDeletePlan = useCallback(async (p) => {
    if (!window.confirm(`¿Eliminar el plan "${p.name}" (${p.plan_number})?\n\nEsto borrará también todos sus hitos, OKRs, backlogs y tareas. Esta acción es irreversible.`)) return;
    try {
      await axios.delete(`${API}/qa/plans/${p.id}`, { headers: authHeaders() });
      toast.success(`Plan ${p.plan_number} eliminado`);
      setPlans(prev => prev.filter(x => x.id !== p.id));
    } catch (e) { toast.error(parseError(e)); }
  }, []);

  const isClient = user?.role === 'client_qa';
  const isAdmin = user && ['admin', 'gerencia', 'contratos', 'finanzas', 'qa'].includes(user.role);

  useEffect(() => {
    const stored = localStorage.getItem('user');
    if (!stored) { navigate('/login'); return; }
    setUser(JSON.parse(stored));
  }, [navigate]);

  const loadPlans = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/qa/plans`, { headers: authHeaders() });
      setPlans(r.data || []);
      if (user?.role === 'client_qa' && r.data?.length === 1) setSelectedPlan(r.data[0]);
    } catch { toast.error('Error al cargar planes'); }
    finally { setLoading(false); }
  }, [user]);

  useEffect(() => { if (user) loadPlans(); }, [user, loadPlans]);
  if (!user) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ShieldCheck className="w-6 h-6 text-[#2F3D8E]" />
            <div>
              <h1 className="font-bold text-[#1a2456]">QA Test Plans</h1>
              <p className="text-xs text-gray-500">Trazabilidad de pruebas con backlog ejecutable</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-600">
              {user.full_name || user.username}
              <span className="ml-2 text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">{user.role}</span>
            </span>
            <Button variant="ghost" size="sm" onClick={() => { localStorage.clear(); navigate('/login'); }}>
              <LogOut className="w-4 h-4 mr-2" /> Salir
            </Button>
          </div>
        </div>
      </header>

      <ModuleKanbanMenu user={user} active="qa" />

      <main className="max-w-7xl mx-auto px-4 py-6">
        {!selectedPlan && (
          <>
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className="text-xl font-bold text-[#1a2456]">{isClient ? 'Su Plan de QA' : 'Planes de QA'}</h2>
                <p className="text-sm text-gray-500">{isClient ? 'Revise progreso, evidencias y firme actas' : 'Cada plan se asocia a un cliente y agrupa sus backlogs'}</p>
              </div>
              {isAdmin && (
                <Button onClick={() => setShowNewPlanModal(true)} className="bg-[#4C8C3B] hover:bg-[#3d7030]" data-testid="new-plan-btn">
                  <Plus className="w-4 h-4 mr-2" /> Nuevo Plan
                </Button>
              )}
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {loading && <div className="col-span-3 text-center py-10 text-gray-400"><Loader2 className="w-6 h-6 animate-spin inline mr-2" />Cargando...</div>}
              {!loading && plans.length === 0 && (
                <div className="col-span-3 text-center py-12 bg-white rounded-2xl border-2 border-dashed">
                  <ShieldCheck className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No hay planes QA creados</p>
                  {isAdmin && <Button className="mt-4 bg-[#2F3D8E]" onClick={() => setShowNewPlanModal(true)}>Crear primer plan</Button>}
                </div>
              )}
              {plans.map(p => {
                const s = p.stats || {};
                return (
                  <div key={p.id} className="bg-white rounded-2xl border hover:shadow-md transition-shadow cursor-pointer"
                       onClick={() => setSelectedPlan(p)} data-testid={`plan-card-${p.plan_number}`}>
                    <div className="p-5">
                      <div className="flex items-start justify-between mb-2">
                        <span className="text-xs font-mono text-gray-500">{p.plan_number}</span>
                        <div className="flex items-center gap-1">
                          <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                            p.status === 'active' ? 'bg-green-100 text-green-700' :
                            p.status === 'completed' ? 'bg-blue-100 text-blue-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>{p.status}</span>
                          {isAdmin && (
                            <>
                              <button onClick={(e) => { e.stopPropagation(); setEditingPlan(p); }}
                                      className="text-gray-400 hover:text-[#2F3D8E] p-0.5"
                                      title="Editar plan"
                                      data-testid={`edit-plan-${p.plan_number}`}>
                                <Edit className="w-3.5 h-3.5" />
                              </button>
                              <button onClick={(e) => { e.stopPropagation(); handleDeletePlan(p); }}
                                      className="text-gray-400 hover:text-red-600 p-0.5"
                                      title="Eliminar plan"
                                      data-testid={`delete-plan-${p.plan_number}`}>
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                      <h3 className="font-bold text-[#1a2456] mb-1">{p.name}</h3>
                      <p className="text-sm text-gray-600 mb-3">{p.client_company_name}</p>
                      {p.description && <p className="text-xs text-gray-500 line-clamp-2 mb-3">{p.description}</p>}
                      <div className="grid grid-cols-4 gap-1 text-center">
                        {[
                          ['bg-green-50','text-green-700','PASS', s.passed||0,'text-green-600'],
                          ['bg-red-50','text-red-700','FAIL', s.failed||0,'text-red-600'],
                          ['bg-amber-50','text-amber-700','BLOCK', s.blocked||0,'text-amber-600'],
                          ['bg-gray-50','text-gray-700','PEND', s.pending||0,'text-gray-600'],
                        ].map(([bg,tc,l,v,lc],i) => (
                          <div key={i} className={`${bg} rounded p-2`}>
                            <div className={`text-lg font-bold ${tc}`}>{v}</div>
                            <div className={`text-[9px] ${lc}`}>{l}</div>
                          </div>
                        ))}
                      </div>
                      <div className="mt-3 text-xs text-gray-500 flex items-center justify-between">
                        <span>{s.total || 0} backlogs</span>
                        <span className="font-bold text-[#2F3D8E]">{s.pass_pct || 0}% pass</span>
                      </div>
                      <div className="h-1.5 bg-gray-200 rounded-full mt-1.5 overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-green-500 to-emerald-500" style={{ width: `${s.pass_pct || 0}%` }}></div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {selectedPlan && (
          <PlanDetail plan={selectedPlan} user={user} isAdmin={isAdmin} isClient={isClient}
                      onBack={() => { setSelectedPlan(null); loadPlans(); }} />
        )}

        {showNewPlanModal && (
          <NewPlanModal onClose={() => setShowNewPlanModal(false)}
                        onCreated={async (p) => { setShowNewPlanModal(false); await loadPlans(); setSelectedPlan(p); }} />
        )}
        {editingPlan && (
          <NewPlanModal plan={editingPlan} onClose={() => setEditingPlan(null)}
                        onCreated={async () => { setEditingPlan(null); await loadPlans(); }} />
        )}
      </main>
    </div>
  );
};

// ============================================================================
// DASHBOARD SUMMARY (KPI cards + project donut + milestones bar chart)
// ============================================================================
const DashboardSummary = ({ dashboard }) => {
  const mini = dashboard.minitasks || { total: 0, done: 0, pending: 0, done_pct: 0 };
  const projectPct = dashboard.project_overall_pct ?? 0;
  const milestones = dashboard.milestones_progress || [];

  // Donut: project completion
  const donutData = [
    { name: 'Completado', value: projectPct, color: '#4C8C3B' },
    { name: 'Pendiente', value: Math.max(0, 100 - projectPct), color: '#E5E7EB' },
  ];

  // Bar chart: milestones progress (backlog_pct, minitask_pct, overall_pct)
  const msChartData = milestones.map(m => ({
    name: m.code,
    fullName: `${m.code} · ${m.name}`,
    Backlogs: m.backlog_pct,
    Tareas: m.minitask_pct,
    Total: m.overall_pct,
  }));

  return (
    <div className="space-y-4 mb-5" data-testid="dashboard-summary">
      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        {[
          { l: 'Backlogs', v: dashboard.total, c: 'bg-blue-50 text-blue-700' },
          { l: 'Pass', v: dashboard.by_status?.passed || 0, c: 'bg-green-50 text-green-700' },
          { l: 'Fail', v: dashboard.by_status?.failed || 0, c: 'bg-red-50 text-red-700' },
          { l: 'Pendientes', v: dashboard.by_status?.pending || 0, c: 'bg-gray-50 text-gray-700' },
          { l: 'Atrasados', v: dashboard.overdue || 0, c: 'bg-amber-50 text-amber-700' },
          { l: 'Tareas', v: `${mini.done}/${mini.total}`, c: 'bg-purple-50 text-purple-700', sub: `${mini.done_pct}%` },
        ].map((s, i) => (
          <div key={i} className={`rounded-xl p-3 ${s.c}`} data-testid={`kpi-${s.l.toLowerCase()}`}>
            <div className="text-2xl font-bold">{s.v}</div>
            <div className="text-xs uppercase tracking-wide flex items-center justify-between">
              <span>{s.l}</span>
              {s.sub && <span className="font-semibold ml-2">{s.sub}</span>}
            </div>
          </div>
        ))}
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Project overall donut */}
        <div className="bg-white rounded-xl border p-4 lg:col-span-1" data-testid="chart-project-overall">
          <div className="flex items-center gap-2 mb-3">
            <Target className="w-4 h-4 text-[#2F3D8E]" />
            <h3 className="font-bold text-sm text-gray-700">Avance Total del Proyecto</h3>
          </div>
          <div className="relative h-48">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={donutData}
                  cx="50%" cy="50%"
                  innerRadius={50} outerRadius={75}
                  startAngle={90} endAngle={-270}
                  dataKey="value"
                  stroke="none"
                >
                  {donutData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => `${v}%`} />
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <div className="text-3xl font-bold text-[#2F3D8E]" data-testid="project-pct">{projectPct}%</div>
              <div className="text-[10px] text-gray-500 uppercase tracking-wide">completado</div>
            </div>
          </div>
          <div className="text-[11px] text-gray-500 text-center mt-2">
            Promedio ponderado: <strong>{dashboard.by_status?.passed || 0}</strong> backlogs aprobados +{' '}
            <strong>{mini.done}</strong> tareas hechas / <strong>{dashboard.total + mini.total}</strong> totales
          </div>
        </div>

        {/* Milestones bar chart */}
        <div className="bg-white rounded-xl border p-4 lg:col-span-2" data-testid="chart-milestones">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-[#4C8C3B]" />
              <h3 className="font-bold text-sm text-gray-700">Avance por Hito</h3>
            </div>
            <span className="text-[10px] text-gray-400">% completado</span>
          </div>
          {milestones.length === 0 ? (
            <div className="h-48 flex items-center justify-center text-gray-400 text-xs italic">
              Aun no hay hitos definidos en este plan
            </div>
          ) : (
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={msChartData} margin={{ top: 8, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={msChartData.length > 4 ? -20 : 0} dy={8} height={40} />
                  <YAxis tick={{ fontSize: 10 }} domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                  <Tooltip
                    formatter={(v) => `${v}%`}
                    labelFormatter={(label) => msChartData.find(m => m.name === label)?.fullName || label}
                    contentStyle={{ fontSize: 11, border: '1px solid #e5e7eb', borderRadius: 6 }}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} iconSize={8} />
                  <Bar dataKey="Backlogs" fill="#5DA5D8" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="Tareas" fill="#A78BFA" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="Total" fill="#4C8C3B" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      {/* Per-milestone progress list (responsive) */}
      {milestones.length > 0 && (
        <div className="bg-white rounded-xl border p-3" data-testid="milestones-progress-list">
          <h3 className="text-xs font-semibold text-gray-600 uppercase mb-2">Detalle por Hito</h3>
          <div className="space-y-2">
            {milestones.map(m => (
              <div key={m.id} className="bg-gray-50 rounded-lg p-2.5 text-xs" data-testid={`ms-row-${m.code}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <span className="font-mono text-[#2F3D8E] font-semibold">{m.code}</span>
                    <span className="ml-2 text-gray-700">{m.name}</span>
                    {m.target_date && <span className="ml-2 text-[10px] text-gray-400">📅 {m.target_date}</span>}
                  </div>
                  <span className="font-bold text-[#4C8C3B]">{m.overall_pct}%</span>
                </div>
                <div className="mt-1.5 grid grid-cols-2 gap-3">
                  <div>
                    <div className="flex justify-between text-[10px] text-gray-500 mb-0.5">
                      <span>Backlogs ({m.backlog_passed}/{m.backlog_total})</span>
                      <span>{m.backlog_pct}%</span>
                    </div>
                    <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-full bg-[#5DA5D8] transition-all" style={{ width: `${m.backlog_pct}%` }}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-[10px] text-gray-500 mb-0.5">
                      <span>Tareas ({m.minitask_done}/{m.minitask_total})</span>
                      <span>{m.minitask_pct}%</span>
                    </div>
                    <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-full bg-[#A78BFA] transition-all" style={{ width: `${m.minitask_pct}%` }}></div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// PLAN DETAIL
// ============================================================================
// ============================================================================
// Hook + Select de Usuarios Asignables (Analytica + client_qa del plan)
// ============================================================================
const useAssignableUsers = (planId) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!planId) return;
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/qa/plans/${planId}/assignable-users`, { headers: authHeaders() })
      .then(r => { if (!cancelled) setUsers(r.data || []); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [planId]);

  return { users, loading };
};

const AssigneeSelect = ({ value, onChange, users, testId, compact = false, allowEmpty = true }) => {
  // value is a username; users is the assignable list. Render groups: Analytica / Cliente.
  const analytica = users.filter(u => u.kind === 'analytica');
  const clients = users.filter(u => u.kind === 'client');
  const baseCls = compact
    ? 'h-7 text-[11px] border rounded px-1 bg-white max-w-[150px] truncate'
    : 'h-8 text-xs border rounded px-1.5 bg-white max-w-[200px] truncate';
  const current = users.find(u => u.username === value);
  return (
    <select
      value={value || ''}
      onChange={e => onChange(e.target.value || null)}
      className={baseCls}
      title={current?.email || (value ? `${value} (legacy)` : 'Sin asignar')}
      data-testid={testId}>
      {allowEmpty && <option value="">— Sin asignar —</option>}
      {analytica.length > 0 && (
        <optgroup label="Analytica">
          {analytica.map(u => (
            <option key={u.username} value={u.username} title={u.email || ''}>
              {u.full_name || u.username}
            </option>
          ))}
        </optgroup>
      )}
      {clients.length > 0 && (
        <optgroup label="Cliente">
          {clients.map(u => (
            <option key={u.username} value={u.username} title={u.email || ''}>
              {u.full_name || u.username}
            </option>
          ))}
        </optgroup>
      )}
      {/* When the current value is a legacy free-text not in the user list, keep it */}
      {value && !users.find(u => u.username === value) && (
        <option value={value}>{value}</option>
      )}
    </select>
  );
};


// ============================================================================
// Activity Flag: bandera roja parpadeante cuando hay actividad sin leer en un backlog
// ============================================================================
const useReadMarkers = (planId) => {
  const [markers, setMarkers] = useState({});
  const [itemMarkers, setItemMarkers] = useState({});
  const reload = useCallback(async () => {
    if (!planId) return;
    try {
      const r = await axios.get(`${API}/qa/plans/${planId}/read-markers`, { headers: authHeaders() });
      setMarkers(r.data.markers || {});
      setItemMarkers(r.data.item_markers || {});
    } catch { /* silent */ }
  }, [planId]);
  useEffect(() => { reload(); }, [reload]);
  const markRead = useCallback(async (backlogId, itemId = null) => {
    try {
      const body = itemId ? { backlog_id: backlogId, item_id: itemId } : { backlog_id: backlogId };
      const r = await axios.post(`${API}/qa/plans/${planId}/read-markers`,
        body, { headers: authHeaders() });
      if (itemId) {
        setItemMarkers(m => ({ ...m, [itemId]: r.data.read_at }));
      } else {
        setMarkers(m => ({ ...m, [backlogId]: r.data.read_at }));
      }
    } catch (e) { toast.error(parseError(e)); }
  }, [planId]);
  return { markers, itemMarkers, markRead, reload };
};

const ActivityFlag = ({ backlog, readAt, currentUsername, onMarkRead, compact = false }) => {
  // Hide if no activity, OR if I am the author of the activity (don't notify myself)
  const lastAt = backlog?.last_activity_at;
  if (!lastAt) return null;
  if (backlog.last_activity_by && backlog.last_activity_by === currentUsername) return null;
  const lastTs = new Date(lastAt).getTime();
  const readTs = readAt ? new Date(readAt).getTime() : 0;
  if (readTs >= lastTs) return null;
  const sizeCls = compact ? 'w-5 h-5' : 'w-6 h-6';
  const iconCls = compact ? 'w-3 h-3' : 'w-3.5 h-3.5';
  // Color by author kind: Analytica employees = blue, client_qa = red.
  // Heuristic uses the optional `last_activity_role` if backend set it; falls back to red.
  const isClient = backlog.last_activity_role === 'client_qa';
  const colorCls = isClient
    ? 'bg-red-500 hover:bg-red-600 ring-red-300'
    : 'bg-blue-500 hover:bg-blue-600 ring-blue-300';
  const kindLabel = {
    edit: 'Edición',
    comment: 'Comentario',
    comment_del: 'Comentario eliminado',
    evidence: 'Nueva evidencia',
    evidence_del: 'Evidencia eliminada',
    checklist_add: 'Nueva tarea',
    checklist_edit: 'Tarea actualizada',
    checklist_delete: 'Tarea eliminada',
    checklist_comment: 'Comentario en tarea',
    checklist_comment_del: 'Comentario en tarea eliminado',
    checklist_evidence: 'Evidencia en tarea',
    checklist_evidence_del: 'Evidencia en tarea eliminada',
    checklist_move_in: 'Tarea recibida',
    checklist_move_out: 'Tarea movida',
    client_accept: 'Aceptación de cliente',
  }[backlog.last_activity_kind] || 'Cambio';
  const whoLabel = isClient ? 'Cliente' : 'Analytica';
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onMarkRead(backlog.id); }}
      className={`${sizeCls} rounded-full text-white shadow-lg flex items-center justify-center flex-shrink-0 animate-pulse ring-2 ${colorCls}`}
      title={`${whoLabel}: ${kindLabel} por ${backlog.last_activity_by || ''} (${new Date(lastAt).toLocaleString('es-EC')}). Click para marcar como visto.`}
      data-testid={`activity-flag-${backlog.code || backlog.id}`}>
      <Flag className={iconCls} fill="currentColor" />
    </button>
  );
};

const ItemActivityFlag = ({ backlogId, item, readAt, currentUsername, onMarkRead }) => {
  // Per-task red/blue flag: client_qa = red, Analytica = blue
  const lastAt = item?.last_activity_at;
  if (!lastAt) return null;
  if (item.last_activity_by && item.last_activity_by === currentUsername) return null;
  const lastTs = new Date(lastAt).getTime();
  const readTs = readAt ? new Date(readAt).getTime() : 0;
  if (readTs >= lastTs) return null;
  const isClient = item.last_activity_role === 'client_qa';
  const colorCls = isClient
    ? 'bg-red-500 hover:bg-red-600 ring-red-300'
    : 'bg-blue-500 hover:bg-blue-600 ring-blue-300';
  const kindLabel = {
    edit: 'Tarea actualizada',
    comment: 'Nuevo comentario',
    evidence: 'Nueva evidencia',
    move: 'Tarea movida',
  }[item.last_activity_kind] || 'Cambio en tarea';
  const whoLabel = isClient ? 'Cliente' : 'Analytica';
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onMarkRead(backlogId, item.id); }}
      className={`w-4 h-4 rounded-full text-white shadow flex items-center justify-center flex-shrink-0 animate-pulse ring-1 ${colorCls}`}
      title={`${whoLabel}: ${kindLabel} por ${item.last_activity_by || ''} (${new Date(lastAt).toLocaleString('es-EC')}). Click para marcar como visto.`}
      data-testid={`task-activity-flag-${item.id}`}>
      <Flag className="w-2.5 h-2.5" fill="currentColor" />
    </button>
  );
};


const PlanDetail = ({ plan, user, isAdmin, isClient, onBack }) => {
  const [dashboard, setDashboard] = useState(null);
  const [showClientAccess, setShowClientAccess] = useState(false);
  const [showAlertRecipients, setShowAlertRecipients] = useState(false);
  const [sendingPlanSummary, setSendingPlanSummary] = useState(false);
  const [exportingXlsx, setExportingXlsx] = useState(false);
  const [tab, setTab] = useState('estructura');
  const [refreshKey, setRefreshKey] = useState(0);

  const reload = useCallback(() => setRefreshKey(k => k + 1), []);

  const exportPlanXlsx = async () => {
    setExportingXlsx(true);
    try {
      const r = await axios.get(`${API}/qa/plans/${plan.id}/export/xlsx`,
        { headers: authHeaders(), responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${plan.plan_number}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Excel del plan QA descargado');
    } catch (e) { toast.error(parseError(e)); }
    finally { setExportingXlsx(false); }
  };

  const sendPlanSummary = async () => {
    if (!window.confirm('Enviar resumen general del plan a los destinatarios configurados?')) return;
    setSendingPlanSummary(true);
    try {
      const r = await axios.post(`${API}/qa/plans/${plan.id}/alerts/send-plan-summary`, {}, { headers: authHeaders() });
      toast.success(`Resumen enviado a ${r.data.recipients?.length || 0} destinatario(s)`);
    } catch (e) { toast.error(parseError(e)); }
    finally { setSendingPlanSummary(false); }
  };

  useEffect(() => {
    axios.get(`${API}/qa/plans/${plan.id}/dashboard`, { headers: authHeaders() })
      .then(r => setDashboard(r.data))
      .catch(() => {});
  }, [plan.id, refreshKey]);

  return (
    <>
      <Button variant="ghost" size="sm" onClick={onBack} className="mb-3">
        <ChevronLeft className="w-4 h-4 mr-1" /> Volver
      </Button>

      <div className="bg-gradient-to-r from-[#2F3D8E] to-[#5DA5D8] rounded-2xl p-5 text-white mb-5">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs text-blue-100 mb-1 flex items-center gap-2">
              <span>{plan.plan_number}</span>
              {isAdmin ? (
                <select value={plan.status || 'active'}
                  onChange={async (e) => {
                    try {
                      await axios.put(`${API}/qa/plans/${plan.id}`, { status: e.target.value }, { headers: authHeaders() });
                      toast.success(`Plan marcado como ${e.target.value}`);
                      plan.status = e.target.value;
                    } catch (err) { toast.error(parseError(err)); }
                  }}
                  className="text-[10px] uppercase tracking-wide bg-white/20 border border-white/30 rounded px-2 py-0.5 cursor-pointer"
                  data-testid="plan-status-select">
                  <option value="active" className="text-gray-800">Active</option>
                  <option value="completed" className="text-gray-800">Completed</option>
                  <option value="archived" className="text-gray-800">Archived</option>
                </select>
              ) : <span className="text-[10px] uppercase bg-white/20 rounded px-2 py-0.5">{plan.status}</span>}
            </div>
            <h2 className="text-2xl font-bold">{plan.name}</h2>
            <p className="text-sm text-blue-100 mt-1">Cliente: <strong>{plan.client_company_name}</strong></p>
          </div>
          {isAdmin && (
            <div className="flex items-center gap-2 flex-wrap">
              <Button variant="outline" size="sm" className="bg-white/10 border-white/30 text-white hover:bg-white/20" onClick={exportPlanXlsx} disabled={exportingXlsx} data-testid="export-plan-xlsx-btn">
                {exportingXlsx ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />} Excel
              </Button>
              <Button variant="outline" size="sm" className="bg-white/10 border-white/30 text-white hover:bg-white/20" onClick={() => setShowAlertRecipients(true)} data-testid="open-alert-recipients-btn">
                <Bell className="w-4 h-4 mr-1.5" /> Destinatarios
              </Button>
              <Button variant="outline" size="sm" className="bg-white/10 border-white/30 text-white hover:bg-white/20" onClick={sendPlanSummary} disabled={sendingPlanSummary} data-testid="send-plan-summary-btn">
                {sendingPlanSummary ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />} Enviar resumen
              </Button>
              <Button variant="outline" size="sm" className="bg-white/10 border-white/30 text-white hover:bg-white/20" onClick={() => setShowClientAccess(true)}>
                <Users className="w-4 h-4 mr-1.5" /> Acceso Cliente
              </Button>
            </div>
          )}
        </div>
      </div>

      {dashboard && <DashboardSummary dashboard={dashboard} />}

      <div className="flex gap-1 bg-white rounded-xl border p-1 mb-4 w-fit flex-wrap">
        <button onClick={() => setTab('estructura')} data-testid="tab-estructura"
                className={`px-4 py-1.5 rounded-lg text-sm font-medium ${tab === 'estructura' ? 'bg-[#2F3D8E] text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
          Estructura
        </button>
        <button onClick={() => setTab('matriz')} data-testid="tab-matriz"
                className={`px-4 py-1.5 rounded-lg text-sm font-medium ${tab === 'matriz' ? 'bg-[#2F3D8E] text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
          Matriz de Pruebas
        </button>
        <button onClick={() => setTab('calendario')} data-testid="tab-calendario"
                className={`px-4 py-1.5 rounded-lg text-sm font-medium ${tab === 'calendario' ? 'bg-[#2F3D8E] text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
          Calendario
        </button>
      </div>

      {tab === 'estructura' && <EstructuraTab plan={plan} user={user} isAdmin={isAdmin} isClient={isClient} onChanged={reload} />}
      {tab === 'matriz' && <MatrizTab plan={plan} user={user} isAdmin={isAdmin} isClient={isClient} onChanged={reload} />}
      {tab === 'calendario' && <CalendarioTab plan={plan} />}

      {showClientAccess && <ClientAccessModal plan={plan} onClose={() => setShowClientAccess(false)} />}
      {showAlertRecipients && <AlertRecipientsModal plan={plan} onClose={() => setShowAlertRecipients(false)} />}
    </>
  );
};

// ============================================================================
// NEW PLAN MODAL (sin cambios funcionales)
// ============================================================================
const NewPlanModal = ({ onClose, onCreated, plan = null }) => {
  const isEdit = !!plan;
  const [form, setForm] = useState({
    name: plan?.name || '',
    description: plan?.description || '',
    client_company_name: plan?.client_company_name || '',
    contract_number: plan?.contract_number || '',
    start_date: plan?.start_date || '',
    end_date: plan?.end_date || '',
    status: plan?.status || 'active',
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!form.name || !form.client_company_name) { toast.error('Nombre y cliente requeridos'); return; }
    setBusy(true);
    try {
      if (isEdit) {
        const r = await axios.put(`${API}/qa/plans/${plan.id}`, form, { headers: authHeaders() });
        toast.success('Plan actualizado');
        onCreated(r.data);
      } else {
        const r = await axios.post(`${API}/qa/plans`, form, { headers: authHeaders() });
        toast.success('Plan creado');
        onCreated(r.data);
      }
    } catch (e) { toast.error(parseError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 overflow-y-auto">
      <div className="bg-white rounded-xl w-full max-w-xl my-8" data-testid="new-plan-modal">
        <div className="p-5 border-b flex items-center justify-between">
          <h3 className="font-bold text-[#1a2456]">{isEdit ? `Editar Plan · ${plan.plan_number}` : 'Nuevo Plan QA'}</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-gray-400" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div><Label>Nombre *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
          <div><Label>Cliente *</Label><Input value={form.client_company_name} onChange={e => setForm({ ...form, client_company_name: e.target.value })} /></div>
          <div><Label>Descripcion</Label><Textarea rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
          <div className="grid grid-cols-3 gap-2">
            <div><Label>Contrato N° (opcional)</Label><Input value={form.contract_number} onChange={e => setForm({ ...form, contract_number: e.target.value })} placeholder="Sin contrato" /></div>
            <div><Label>Inicio</Label><Input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} /></div>
            <div><Label>Fin</Label><Input type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} /></div>
          </div>
          {isEdit && (
            <div>
              <Label>Estado</Label>
              <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}
                      className="h-9 w-full border rounded px-2 text-sm">
                <option value="active">Activo</option>
                <option value="completed">Completado</option>
                <option value="archived">Archivado</option>
              </select>
            </div>
          )}
        </div>
        <div className="p-4 border-t bg-gray-50 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button onClick={save} disabled={busy} className="bg-[#2F3D8E] hover:bg-[#1f2d6e] text-white" data-testid="save-plan-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5"/> : null}{isEdit ? 'Guardar' : 'Crear'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// CLIENT ACCESS MODAL (sin cambios)
// ============================================================================
const ClientAccessModal = ({ plan, onClose }) => {
  const [accesses, setAccesses] = useState([]);
  const [form, setForm] = useState({ full_name: '', username: '', password: '', email: '' });
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/qa/plans/${plan.id}/client-accesses`, { headers: authHeaders() });
      setAccesses(r.data || []);
    } catch { toast.error('Error'); }
  }, [plan.id]);
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    if (!form.full_name || !form.username || !form.password) { toast.error('Campos requeridos'); return; }
    setBusy(true);
    try {
      const payload = { plan_id: plan.id, full_name: form.full_name, username: form.username, password: form.password };
      if (form.email?.trim()) payload.email = form.email.trim();
      await axios.post(`${API}/qa/plans/${plan.id}/client-access`, payload, { headers: authHeaders() });
      toast.success('Acceso creado');
      setForm({ full_name: '', username: '', password: '', email: '' });
      load();
    } catch (e) { toast.error(parseError(e)); }
    finally { setBusy(false); }
  };
  const remove = async (u) => {
    if (!window.confirm(`Eliminar acceso ${u.username}?`)) return;
    try {
      await axios.delete(`${API}/qa/client-access/${u.username}`, { headers: authHeaders() });
      toast.success('Eliminado'); load();
    } catch { toast.error('Error'); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl w-full max-w-xl">
        <div className="p-5 border-b flex items-center justify-between">
          <h3 className="font-bold text-[#1a2456]">Acceso del Cliente — {plan.client_company_name}</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-gray-400" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5 text-xs text-blue-900">
            Cada acceso permite al cliente entrar a la app y revisar UNICAMENTE este plan QA.
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Nombre completo</Label><Input value={form.full_name} onChange={e => setForm({ ...form, full_name: e.target.value })} /></div>
            <div><Label>Username</Label><Input value={form.username} onChange={e => setForm({ ...form, username: e.target.value })} /></div>
            <div><Label>Password</Label><Input type="text" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} /></div>
            <div><Label>Email (opcional)</Label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
          </div>
          <Button onClick={create} disabled={busy} className="bg-[#4C8C3B] hover:bg-[#3d7030] text-white">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5"/> : <Plus className="w-4 h-4 mr-1.5"/>}Crear acceso
          </Button>
          <div className="border-t pt-3">
            <h4 className="text-sm font-semibold mb-2">Accesos creados</h4>
            {accesses.length === 0 && <p className="text-xs text-gray-400">Ninguno</p>}
            {accesses.map(a => (
              <div key={a.username} className="flex items-center justify-between border-b py-2 text-sm">
                <div>
                  <strong>{a.full_name}</strong> — <span className="font-mono text-xs">{a.username}</span>
                  {a.email && <span className="text-xs text-gray-500 ml-2">({a.email})</span>}
                </div>
                <Button size="sm" variant="ghost" className="text-red-500" onClick={() => remove(a)}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// ALERT RECIPIENTS MODAL — per-plan email recipients (multiple)
// ============================================================================
const AlertRecipientsModal = ({ plan, onClose }) => {
  const [recipients, setRecipients] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/qa/plans/${plan.id}/alert-recipients`, { headers: authHeaders() });
        setRecipients(r.data.recipients || []);
      } catch (e) { toast.error(parseError(e)); }
      finally { setLoading(false); }
    })();
  }, [plan.id]);

  const add = () => {
    const e = newEmail.trim().toLowerCase();
    if (!e || !e.includes('@')) { toast.error('Email invalido'); return; }
    if (recipients.includes(e)) { toast.error('Ya existe'); return; }
    setRecipients(r => [...r, e]);
    setNewEmail('');
  };
  const remove = (e) => setRecipients(r => r.filter(x => x !== e));

  const save = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/qa/plans/${plan.id}/alert-recipients`,
        { recipients }, { headers: authHeaders() });
      toast.success(`${recipients.length} destinatario(s) guardado(s)`);
      onClose();
    } catch (e) { toast.error(parseError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="alert-recipients-modal">
      <div className="bg-white rounded-xl w-full max-w-xl shadow-xl">
        <div className="p-5 border-b flex items-center justify-between bg-gradient-to-r from-[#2F3D8E] to-[#5DA5D8] text-white rounded-t-xl">
          <div className="flex items-center gap-2">
            <Bell className="w-5 h-5" />
            <h3 className="font-bold">Destinatarios de alertas QA</h3>
          </div>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900 space-y-1">
            <p><strong>Plan:</strong> {plan.plan_number} · {plan.client_company_name}</p>
            <p>Estos emails recibiran <strong>alertas automaticas</strong> (backlog completado, nueva tarea al cliente, OKR completado, acta enviada) y los envios manuales del boton "Enviar resumen". Los <code>client_qa</code> con email ya estan incluidos automaticamente.</p>
          </div>

          {loading ? (
            <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin text-gray-400 mx-auto" /></div>
          ) : (
            <>
              <div>
                <Label className="text-xs">Agregar destinatario</Label>
                <div className="flex gap-2 mt-1">
                  <Input type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)}
                         onKeyDown={e => { if (e.key === 'Enter') add(); }}
                         placeholder="ej: contacto@cliente.com" data-testid="new-recipient-input" />
                  <Button onClick={add} className="bg-[#4C8C3B] hover:bg-[#3d7030] text-white" data-testid="add-recipient-btn">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div>
                <Label className="text-xs">Destinatarios actuales ({recipients.length})</Label>
                {recipients.length === 0 ? (
                  <p className="text-xs text-gray-400 italic py-2">Aun no hay destinatarios. Los <code>client_qa</code> con email se agregan automaticamente.</p>
                ) : (
                  <ul className="space-y-1.5 mt-1 max-h-64 overflow-y-auto">
                    {recipients.map(e => (
                      <li key={e} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2 border" data-testid={`recipient-row-${e}`}>
                        <span className="text-sm font-mono">{e}</span>
                        <button onClick={() => remove(e)} className="text-red-400 hover:text-red-600">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          )}
        </div>
        <div className="p-4 border-t bg-gray-50 flex justify-end gap-2 rounded-b-xl">
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button onClick={save} disabled={busy} className="bg-[#2F3D8E] hover:bg-[#1f2d6e] text-white" data-testid="save-recipients-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Save className="w-4 h-4 mr-1.5" />}
            Guardar
          </Button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// REUSABLE: Inline editable text
// ============================================================================
const InlineEdit = ({ value, onSave, placeholder, multiline = false, type = 'text', className = '', testId, canEdit = true, displayClassName = '' }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value || '');
  useEffect(() => { setVal(value || ''); }, [value]);
  const commit = async () => {
    setEditing(false);
    if ((val || '') === (value || '')) return;
    try { await onSave(val); } catch (e) { toast.error(parseError(e)); setVal(value || ''); }
  };
  if (!canEdit) return <span className={displayClassName}>{value || placeholder || '-'}</span>;
  if (editing) {
    if (multiline) {
      return (
        <Textarea autoFocus value={val} onChange={e => setVal(e.target.value)} onBlur={commit}
                  rows={3} className={`${className}`} data-testid={testId} />
      );
    }
    return (
      <Input autoFocus type={type} value={val} onChange={e => setVal(e.target.value)}
             onBlur={commit} onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setVal(value); setEditing(false); } }}
             className={`h-7 ${className}`} data-testid={testId} />
    );
  }
  return (
    <span onClick={() => setEditing(true)} className={`cursor-pointer hover:bg-yellow-50 px-1 rounded ${displayClassName}`} title="Click para editar">
      {value || <span className="italic text-gray-400">{placeholder || 'Click para editar'}</span>}
    </span>
  );
};

// ============================================================================
// Pastel color picker per author (keeps same color for same author across thread)
// ============================================================================
const AUTHOR_PALETTE = [
  { bg: '#fee2e2', fg: '#991b1b', border: '#fca5a5' },
  { bg: '#ffedd5', fg: '#9a3412', border: '#fdba74' },
  { bg: '#fef3c7', fg: '#92400e', border: '#fcd34d' },
  { bg: '#dcfce7', fg: '#166534', border: '#86efac' },
  { bg: '#cffafe', fg: '#155e75', border: '#67e8f9' },
  { bg: '#dbeafe', fg: '#1e40af', border: '#93c5fd' },
  { bg: '#ede9fe', fg: '#5b21b6', border: '#c4b5fd' },
  { bg: '#fce7f3', fg: '#9d174d', border: '#f9a8d4' },
];
const authorColor = (name) => {
  const s = (name || '').toString();
  let hash = 0;
  for (let i = 0; i < s.length; i++) hash = (hash << 5) - hash + s.charCodeAt(i);
  return AUTHOR_PALETTE[Math.abs(hash) % AUTHOR_PALETTE.length];
};
const AuthorFlag = ({ author, role, createdAt, compact = false }) => {
  const c = authorColor(author);
  const initials = (author || '?').split(/\s+/).map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase() || '?';
  const padCls = compact ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-0.5 text-[11px]';
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border font-semibold ${padCls}`}
          style={{ backgroundColor: c.bg, color: c.fg, borderColor: c.border }}
          title={`${author || ''}${role ? ' · ' + role : ''}${createdAt ? ' · ' + new Date(createdAt).toLocaleString('es-EC') : ''}`}
          data-testid={`author-flag-${(author || '').toString().toLowerCase().replace(/\s+/g, '-')}`}>
      <Flag className="w-3 h-3" style={{ color: c.fg }} />
      <span className="font-mono text-[9px] opacity-70">{initials}</span>
      <span className="truncate max-w-[120px]">{author || 'Anon'}</span>
    </span>
  );
};

// ============================================================================
// REUSABLE: Comments thread
// ============================================================================
const CommentsThread = ({ entityType, entityId, comments = [], user, canAdd = true, onChanged }) => {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const add = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/qa/${entityType}/${entityId}/comments`, { text: text.trim() }, { headers: authHeaders() });
      setText('');
      onChanged?.();
    } catch (e) { toast.error(parseError(e)); }
    finally { setBusy(false); }
  };
  const remove = async (c) => {
    try {
      await axios.delete(`${API}/qa/${entityType}/${entityId}/comments/${c.id}`, { headers: authHeaders() });
      onChanged?.();
    } catch (e) { toast.error(parseError(e)); }
  };
  return (
    <div className="space-y-1.5" data-testid={`comments-${entityType}-${entityId}`}>
      <div className="text-xs font-semibold text-gray-600 uppercase flex items-center gap-1.5"><MessageSquare className="w-3 h-3" />Comentarios ({comments.length})</div>
      {comments.length === 0 && <p className="text-xs text-gray-400 italic">Sin comentarios.</p>}
      <div className="space-y-1.5">
        {comments.map(c => (
          <div key={c.id} className="bg-gray-50 rounded-md p-2 text-xs flex items-start gap-2"
               style={{ borderLeft: `3px solid ${authorColor(c.author).border}` }}>
            <div className="flex-1 space-y-1">
              <div className="flex items-center gap-2 flex-wrap">
                <AuthorFlag author={c.author} role={c.author_role} createdAt={c.created_at} />
                <span className="text-gray-400 text-[10px]">{c.author_role || ''} · {new Date(c.created_at).toLocaleString('es-EC')}</span>
              </div>
              <div className="text-gray-700 whitespace-pre-wrap">{c.text}</div>
            </div>
            {(c.author === user?.username || c.author === user?.full_name) && (
              <button onClick={() => remove(c)} className="text-red-400 hover:text-red-600" title="Eliminar">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        ))}
      </div>
      {canAdd && (
        <div className="flex gap-2 mt-2">
          <Input value={text} onChange={e => setText(e.target.value)} placeholder="Escribe un comentario..." className="h-8 text-xs"
                 onKeyDown={e => { if (e.key === 'Enter') add(); }} data-testid={`comment-input-${entityType}-${entityId}`} />
          <Button size="sm" onClick={add} disabled={busy || !text.trim()} className="h-8 bg-[#2F3D8E] hover:bg-[#1f2d6e]">
            {busy ? <Loader2 className="w-3 h-3 animate-spin"/> : <Plus className="w-3 h-3" />}
          </Button>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// REUSABLE: Evidence gallery
// ============================================================================
const EvidenceGallery = ({ entityType, entityId, evidence = [], user, canAdd = true, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    setBusy(true);
    for (const f of files) {
      try {
        const data_url = await readFileAsDataUrl(f);
        await axios.post(`${API}/qa/${entityType}/${entityId}/evidence`,
          { filename: f.name, data_url, note: '' }, { headers: authHeaders() });
      } catch (err) { toast.error(parseError(err, err.message)); }
    }
    e.target.value = '';
    setBusy(false);
    onChanged?.();
  };
  const remove = async (ev) => {
    if (!window.confirm(`Eliminar "${ev.filename}"?`)) return;
    try {
      await axios.delete(`${API}/qa/${entityType}/${entityId}/evidence/${ev.id}`, { headers: authHeaders() });
      onChanged?.();
    } catch (e) { toast.error(parseError(e)); }
  };
  return (
    <div className="space-y-2" data-testid={`evidence-${entityType}-${entityId}`}>
      <div className="text-xs font-semibold text-gray-600 uppercase flex items-center gap-1.5">
        <ImageIcon className="w-3 h-3" />Evidencias ({evidence.length})
        {canAdd && (
          <label className="ml-auto cursor-pointer text-[#2F3D8E] hover:underline normal-case font-normal flex items-center gap-1">
            <Upload className="w-3 h-3" /> {busy ? 'Subiendo...' : 'Agregar (img/PDF)'}
            <input type="file" accept="image/*,application/pdf" multiple onChange={handleFiles} className="hidden" disabled={busy} data-testid={`evidence-input-${entityType}-${entityId}`} />
          </label>
        )}
      </div>
      {evidence.length === 0 && <p className="text-xs text-gray-400 italic">Sin evidencias.</p>}
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
        {evidence.map(ev => {
          const url = evidenceUrl(ev);
          const isPdf = isPdfEvidence(ev);
          const canRemove = ev.uploaded_by === user?.username || ev.uploaded_by === user?.full_name;
          return (
            <div key={ev.id || ev.filename} className="relative group" data-testid={`ev-${ev.id || ev.filename}`}>
              {isPdf ? (
                <a href={url} target="_blank" rel="noreferrer"
                   className="block w-full h-20 bg-red-50 hover:bg-red-100 rounded border border-red-200 flex flex-col items-center justify-center text-red-700 transition-colors p-1"
                   data-testid={`ev-pdf-link-${ev.id || ev.filename}`}>
                  <FileText className="w-7 h-7" />
                  <span className="text-[9px] font-semibold mt-0.5">PDF</span>
                </a>
              ) : url ? (
                <a href={url} target="_blank" rel="noreferrer">
                  <img src={url} alt={ev.filename} className="w-full h-20 object-cover rounded border" />
                </a>
              ) : (
                <div className="w-full h-20 bg-gray-100 rounded border flex items-center justify-center text-gray-400 text-[10px]">{ev.filename}</div>
              )}
              <div className="text-[10px] text-gray-500 truncate" title={ev.filename}>{ev.filename}</div>
              {ev.note && <div className="text-[10px] text-gray-400 italic truncate" title={ev.note}>{ev.note}</div>}
              {canRemove && (
                <button onClick={() => remove(ev)}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 text-[10px] opacity-0 group-hover:opacity-100"
                        title="Eliminar">×</button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ============================================================================
// REUSABLE: Checklist (con tareas expandibles con sus propias evidencias y comentarios)
// ============================================================================
const Checklist = ({ backlogId, checklist = [], user, isClient, canManage = true, onChanged, clientCompanyName = 'Cliente', assignableUsers = [], itemMarkers = {}, onMarkItemRead = null, moveBacklogTargets = [] }) => {
  const [newTitle, setNewTitle] = useState('');
  const [newResponsibleOrg, setNewResponsibleOrg] = useState('analytica');
  const [newDue, setNewDue] = useState('');
  const [newAssignedTo, setNewAssignedTo] = useState('');
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState({});

  const add = async () => {
    if (!newTitle.trim()) return;
    setBusy(true);
    try {
      // Resolve assigned_to: explicit username preferred; fallback to responsible org label
      const assignedTo = newAssignedTo
        ? newAssignedTo
        : (newResponsibleOrg === 'analytica' ? 'Analytica' : clientCompanyName);
      await axios.post(`${API}/qa/backlogs/${backlogId}/checklist`,
        { title: newTitle.trim(), assigned_to: assignedTo, due_date: newDue || null, responsible_org: newResponsibleOrg },
        { headers: authHeaders() });
      setNewTitle(''); setNewDue(''); setNewResponsibleOrg('analytica'); setNewAssignedTo('');
      onChanged?.();
    } catch (e) { toast.error(parseError(e)); }
    finally { setBusy(false); }
  };
  const toggle = async (item) => {
    try {
      await axios.put(`${API}/qa/backlogs/${backlogId}/checklist/${item.id}`, { done: !item.done }, { headers: authHeaders() });
      onChanged?.();
    } catch (e) { toast.error(parseError(e)); }
  };
  const updateField = async (item, patch) => {
    try {
      // Si cambia responsible_org, sincronizar assigned_to
      if (patch.responsible_org && !patch.assigned_to) {
        patch.assigned_to = patch.responsible_org === 'analytica' ? 'Analytica' : clientCompanyName;
      }
      await axios.put(`${API}/qa/backlogs/${backlogId}/checklist/${item.id}`, patch, { headers: authHeaders() });
      onChanged?.();
    } catch (e) { toast.error(parseError(e)); }
  };
  const remove = async (item) => {
    try {
      await axios.delete(`${API}/qa/backlogs/${backlogId}/checklist/${item.id}`, { headers: authHeaders() });
      onChanged?.();
    } catch (e) { toast.error(parseError(e)); }
  };
  const moveItem = async (item, targetBacklogId) => {
    if (!targetBacklogId || targetBacklogId === backlogId) return;
    if (!window.confirm('¿Mover esta tarea al backlog seleccionado?')) return;
    try {
      await axios.put(`${API}/qa/backlogs/${backlogId}/checklist/${item.id}/move`,
        { target_backlog_id: targetBacklogId }, { headers: authHeaders() });
      toast.success('Tarea movida');
      onChanged?.();
    } catch (e) { toast.error(parseError(e)); }
  };
  const addComment = async (item, text) => {
    if (!text.trim()) return;
    try {
      await axios.post(`${API}/qa/backlogs/${backlogId}/checklist/${item.id}/comments`,
        { text: text.trim() }, { headers: authHeaders() });
      onChanged?.();
    } catch (e) { toast.error(parseError(e)); }
  };
  const removeComment = async (item, commentId) => {
    try {
      await axios.delete(`${API}/qa/backlogs/${backlogId}/checklist/${item.id}/comments/${commentId}`, { headers: authHeaders() });
      onChanged?.();
    } catch (e) { toast.error(parseError(e)); }
  };
  const handleEvidenceFiles = async (item, files) => {
    for (const f of files) {
      try {
        const data_url = await readFileAsDataUrl(f);
        await axios.post(`${API}/qa/backlogs/${backlogId}/checklist/${item.id}/evidence`,
          { filename: f.name, data_url, note: '' }, { headers: authHeaders() });
      } catch (err) { toast.error(parseError(err, err.message)); }
    }
    onChanged?.();
  };
  const removeEvidence = async (item, evId) => {
    try {
      await axios.delete(`${API}/qa/backlogs/${backlogId}/checklist/${item.id}/evidence/${evId}`, { headers: authHeaders() });
      onChanged?.();
    } catch (e) { toast.error(parseError(e)); }
  };

  const doneCount = checklist.filter(c => c.done).length;
  const progressPct = checklist.length > 0 ? Math.round(doneCount / checklist.length * 100) : 0;

  return (
    <div className="space-y-2" data-testid={`checklist-${backlogId}`}>
      <div className="text-xs font-semibold text-gray-600 uppercase flex items-center gap-1.5">
        <ListChecks className="w-3 h-3" /> Checklist ({doneCount}/{checklist.length}) · {progressPct}%
      </div>
      {checklist.length > 0 && (
        <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
          <div className="h-full bg-[#4C8C3B]" style={{ width: `${progressPct}%` }}></div>
        </div>
      )}
      <div className="space-y-1">
        {checklist.length === 0 && <p className="text-xs text-gray-400 italic">Sin tareas.</p>}
        {checklist.map(item => {
          const isOpen = !!expanded[item.id];
          const isAnalytica = (item.responsible_org || 'analytica') === 'analytica';
          const evCount = (item.evidence || []).length;
          const coCount = (item.comments || []).length;
          return (
            <div key={item.id} className={`text-xs rounded border ${item.done ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'}`}>
              <div className="flex items-center gap-1.5 py-1 px-2 flex-wrap">
                <button onClick={() => toggle(item)} className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${item.done ? 'bg-green-500 border-green-500' : 'bg-white border-gray-300 hover:border-green-500'}`}
                        data-testid={`checklist-toggle-${item.id}`}>
                  {item.done && <Check className="w-3 h-3 text-white" />}
                </button>
                {onMarkItemRead && (
                  <ItemActivityFlag backlogId={backlogId} item={item}
                                    readAt={itemMarkers[item.id]}
                                    currentUsername={user?.sub || user?.username}
                                    onMarkRead={onMarkItemRead} />
                )}
                {item.full_code && <span className={`font-mono text-[9px] ${isAnalytica ? 'text-blue-600' : 'text-orange-600'} font-semibold`}>{item.full_code.split('-').slice(-1)[0]}</span>}
                <div className={`flex-1 min-w-[120px] ${item.done ? 'line-through text-gray-400' : ''}`}>
                  {canManage ? (
                    <InlineEdit value={item.title} onSave={(v) => updateField(item, { title: v })} testId={`checklist-title-${item.id}`} />
                  ) : (
                    <span>{item.title}</span>
                  )}
                </div>
                {canManage && (
                  <>
                    <select value={item.responsible_org || 'analytica'}
                            onChange={e => updateField(item, { responsible_org: e.target.value })}
                            className={`text-[10px] border rounded px-1 max-w-[80px] truncate ${isAnalytica ? 'bg-blue-50 text-blue-700' : 'bg-orange-50 text-orange-700'}`}
                            title={isAnalytica ? 'Analytica' : clientCompanyName}
                            data-testid={`checklist-org-${item.id}`}>
                      <option value="analytica">Analytica</option>
                      <option value="client">{clientCompanyName}</option>
                    </select>
                    <AssigneeSelect value={item.assigned_to}
                                    onChange={(v) => updateField(item, { assigned_to: v })}
                                    users={assignableUsers} compact
                                    testId={`checklist-assignee-${item.id}`} />
                    <InlineEdit value={item.due_date} onSave={(v) => updateField(item, { due_date: v || null })}
                                placeholder="due" type="date" className="w-28" displayClassName="text-[10px] text-gray-500" />
                  </>
                )}
                {!canManage && (
                  <>
                    <span className={`text-[10px] font-medium ${isAnalytica ? 'text-blue-700' : 'text-orange-700'}`}>
                      {isAnalytica ? 'Analytica' : clientCompanyName}
                    </span>
                    {item.assigned_to && <span className="text-[10px] text-gray-700">👤 {item.assigned_to}</span>}
                    {item.due_date && <span className="text-[10px] text-gray-500">{item.due_date}</span>}
                  </>
                )}
                <button onClick={() => setExpanded(p => ({ ...p, [item.id]: !isOpen }))}
                        className={`text-[10px] px-1.5 py-0.5 rounded ${(evCount + coCount) > 0 ? 'bg-purple-100 text-purple-700 hover:bg-purple-200' : 'text-gray-400 hover:text-gray-700'}`}
                        title="Comentarios y evidencias">
                  <MessageSquare className="w-3 h-3 inline" /> {coCount + evCount > 0 ? coCount + evCount : ''}
                </button>
                {canManage && moveBacklogTargets.length > 0 && (
                  <select value=""
                          onChange={e => { if (e.target.value) { moveItem(item, e.target.value); e.target.value = ''; } }}
                          className="text-[10px] border rounded px-1 max-w-[110px] bg-amber-50 text-amber-800"
                          title="Mover a otro backlog"
                          data-testid={`checklist-move-${item.id}`}>
                    <option value="">↗ Mover</option>
                    {moveBacklogTargets.map(t => (
                      <option key={t.id} value={t.id}>{t.label}</option>
                    ))}
                  </select>
                )}
                {canManage && (() => {
                  // Clients can only delete checklist items they created themselves
                  const canDelete = !isClient || (item.created_by === user?.sub && item.created_by_role === 'client_qa');
                  return canDelete ? (
                    <button onClick={() => remove(item)} className="text-red-400 hover:text-red-600" title="Eliminar">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  ) : null;
                })()}
              </div>
              {isOpen && (
                <div className="border-t bg-gray-50 px-2 py-2 space-y-2">
                  {item.full_code && <div className="font-mono text-[10px] text-gray-500">Codigo: {item.full_code}</div>}
                  {/* Comentarios */}
                  <ChecklistMiniComments
                    item={item}
                    user={user}
                    onAdd={(text) => addComment(item, text)}
                    onRemove={(commentId) => removeComment(item, commentId)} />
                  {/* Evidencias */}
                  <ChecklistMiniEvidence
                    item={item}
                    user={user}
                    onUpload={(files) => handleEvidenceFiles(item, files)}
                    onRemove={(evId) => removeEvidence(item, evId)} />
                </div>
              )}
            </div>
          );
        })}
      </div>
      {canManage && (
        <div className="flex flex-wrap items-center gap-1 mt-1">
          <Input className="h-8 text-xs flex-1 min-w-[160px]" placeholder="Nueva tarea..." value={newTitle} onChange={e => setNewTitle(e.target.value)}
                 onKeyDown={e => { if (e.key === 'Enter') add(); }} data-testid={`checklist-new-title-${backlogId}`} />
          <select className="h-8 text-xs border rounded px-1 max-w-[110px]" value={newResponsibleOrg} onChange={e => setNewResponsibleOrg(e.target.value)}
                  title={newResponsibleOrg === 'analytica' ? 'Analytica' : clientCompanyName}>
            <option value="analytica">Analytica</option>
            <option value="client">{clientCompanyName}</option>
          </select>
          <AssigneeSelect value={newAssignedTo} onChange={(v) => setNewAssignedTo(v || '')}
                          users={assignableUsers} compact testId={`checklist-new-assignee-${backlogId}`} />
          <Input className="h-8 text-xs w-32" type="date" value={newDue} onChange={e => setNewDue(e.target.value)} />
          <Button size="sm" className="h-8 bg-[#4C8C3B] hover:bg-[#3d7030]" onClick={add} disabled={busy}>
            {busy ? <Loader2 className="w-3 h-3 animate-spin"/> : <Plus className="w-3 h-3" />}
          </Button>
        </div>
      )}
    </div>
  );
};

const ChecklistMiniComments = ({ item, user, onAdd, onRemove }) => {
  const [text, setText] = useState('');
  const comments = item.comments || [];
  return (
    <div>
      <div className="text-[10px] font-semibold text-gray-600 uppercase mb-1 flex items-center gap-1">
        <MessageSquare className="w-3 h-3" /> Comentarios ({comments.length})
      </div>
      {comments.length === 0 && <p className="text-[10px] text-gray-400 italic">Sin comentarios.</p>}
      <div className="space-y-1">
        {comments.map(c => (
          <div key={c.id} className="bg-white rounded p-1.5 flex items-start gap-1 text-[10px]"
               style={{ borderLeft: `3px solid ${authorColor(c.author).border}` }}>
            <div className="flex-1 space-y-0.5">
              <div className="flex items-center gap-1 flex-wrap">
                <AuthorFlag author={c.author} role={c.author_role} createdAt={c.created_at} compact />
                <span className="text-gray-400 text-[9px]">{new Date(c.created_at).toLocaleString('es-EC')}</span>
              </div>
              <div className="text-gray-700">{c.text}</div>
            </div>
            {(c.author === user?.username || c.author === user?.full_name) && (
              <button onClick={() => onRemove(c.id)} className="text-red-400 hover:text-red-600"><X className="w-3 h-3" /></button>
            )}
          </div>
        ))}
      </div>
      <div className="flex gap-1 mt-1">
        <Input className="flex-1 h-7 text-[10px]" placeholder="Comentario..." value={text} onChange={e => setText(e.target.value)}
               onKeyDown={e => { if (e.key === 'Enter') { onAdd(text); setText(''); } }} />
        <Button size="sm" className="h-7 px-2 bg-[#2F3D8E] hover:bg-[#1f2d6e]" onClick={() => { onAdd(text); setText(''); }} disabled={!text.trim()}>
          <Plus className="w-3 h-3" />
        </Button>
      </div>
    </div>
  );
};

const ChecklistMiniEvidence = ({ item, user, onUpload, onRemove }) => {
  const evidence = item.evidence || [];
  return (
    <div>
      <div className="text-[10px] font-semibold text-gray-600 uppercase mb-1 flex items-center gap-1">
        <ImageIcon className="w-3 h-3" /> Evidencias ({evidence.length})
        <label className="ml-auto cursor-pointer text-[#2F3D8E] hover:underline normal-case font-normal flex items-center gap-1">
          <Upload className="w-3 h-3" /> Agregar (img/PDF)
          <input type="file" accept="image/*,application/pdf" multiple className="hidden"
                 onChange={(e) => { onUpload(Array.from(e.target.files || [])); e.target.value = ''; }}
                 data-testid={`mini-evidence-input-${item.id}`} />
        </label>
      </div>
      {evidence.length === 0 && <p className="text-[10px] text-gray-400 italic">Sin evidencias.</p>}
      <div className="grid grid-cols-4 gap-1">
        {evidence.map(ev => {
          const url = evidenceUrl(ev);
          const isPdf = isPdfEvidence(ev);
          const canRemove = ev.uploaded_by === user?.username || ev.uploaded_by === user?.full_name;
          return (
            <div key={ev.id} className="relative group" data-testid={`mini-ev-${ev.id}`}>
              {isPdf ? (
                <a href={url} target="_blank" rel="noreferrer"
                   className="block w-full h-16 bg-red-50 hover:bg-red-100 rounded border border-red-200 flex flex-col items-center justify-center text-red-700 transition-colors">
                  <FileText className="w-5 h-5" />
                  <span className="text-[8px] font-semibold">PDF</span>
                </a>
              ) : url ? (
                <a href={url} target="_blank" rel="noreferrer">
                  <img src={url} alt={ev.filename} className="w-full h-16 object-cover rounded border" />
                </a>
              ) : (
                <div className="w-full h-16 bg-gray-100 rounded border flex items-center justify-center text-[10px] text-gray-400">{ev.filename}</div>
              )}
              <div className="text-[9px] text-gray-500 truncate" title={ev.filename}>{ev.filename}</div>
              {canRemove && (
                <button onClick={() => onRemove(ev.id)} className="absolute top-0 right-0 bg-red-500 text-white rounded-full w-4 h-4 text-[9px] opacity-0 group-hover:opacity-100">×</button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ============================================================================
// ESTRUCTURA TAB (full edit por usuarios internos; cliente solo lectura)
// ============================================================================
const EstructuraTab = ({ plan, user, isAdmin, isClient, onChanged }) => {
  // Clients can collaborate on backlogs + checklist (create/edit always; delete only own)
  const canEditBacklog = isAdmin || isClient;
  const { users: assignableUsers } = useAssignableUsers(plan.id);
  const { markers: readMarkers, itemMarkers, markRead } = useReadMarkers(plan.id);
  const [milestones, setMilestones] = useState([]);
  const [okrs, setOkrs] = useState([]);
  const [backlogs, setBacklogs] = useState([]);
  const [expandedMs, setExpandedMs] = useState({});
  const [expandedOkr, setExpandedOkr] = useState({});
  const [expandedBl, setExpandedBl] = useState({});
  const [signingActaOkr, setSigningActaOkr] = useState(null);

  const load = useCallback(async () => {
    try {
      const [m, o, b] = await Promise.all([
        axios.get(`${API}/qa/plans/${plan.id}/milestones`, { headers: authHeaders() }),
        axios.get(`${API}/qa/plans/${plan.id}/okrs`, { headers: authHeaders() }),
        axios.get(`${API}/qa/plans/${plan.id}/backlogs`, { headers: authHeaders() }),
      ]);
      setMilestones(m.data); setOkrs(o.data); setBacklogs(b.data);
      onChanged?.();
    } catch (e) { toast.error(parseError(e)); }
  }, [plan.id, onChanged]);

  useEffect(() => { load(); }, [load]);

  const updateMs = async (id, patch) => {
    await axios.put(`${API}/qa/milestones/${id}`, patch, { headers: authHeaders() });
    load();
  };
  const updateOkr = async (id, patch) => {
    await axios.put(`${API}/qa/okrs/${id}`, patch, { headers: authHeaders() });
    load();
  };
  const updateBl = async (id, patch) => {
    await axios.put(`${API}/qa/backlogs/${id}`, patch, { headers: authHeaders() });
    load();
  };

  const createMs = async () => {
    const name = window.prompt('Nombre del hito:');
    if (!name) return;
    try {
      await axios.post(`${API}/qa/plans/${plan.id}/milestones`, { name, order_index: milestones.length }, { headers: authHeaders() });
      toast.success('Hito creado'); load();
    } catch (e) { toast.error(parseError(e)); }
  };
  const createOkr = async (msId) => {
    const objective = window.prompt('Objetivo del OKR:');
    if (!objective) return;
    try {
      await axios.post(`${API}/qa/plans/${plan.id}/okrs`, { milestone_id: msId, objective }, { headers: authHeaders() });
      toast.success('OKR creado'); load();
    } catch (e) { toast.error(parseError(e)); }
  };
  const createBl = async (okrId, test_type = 'programacion') => {
    const title = window.prompt(`Titulo del backlog (${test_type === 'programacion' ? 'Programacion' : 'Usuario'}):`);
    if (!title) return;
    try {
      await axios.post(`${API}/qa/plans/${plan.id}/backlogs`,
        { okr_id: okrId, title, priority: 'medium', test_type }, { headers: authHeaders() });
      toast.success('Backlog creado'); load();
    } catch (e) { toast.error(parseError(e)); }
  };
  const deleteItem = async (kind, id, label) => {
    if (!window.confirm(`Eliminar ${label}? ${kind === 'milestones' || kind === 'okrs' ? 'Se borraran los hijos.' : ''}`)) return;
    try {
      await axios.delete(`${API}/qa/${kind}/${id}`, { headers: authHeaders() });
      toast.success('Eliminado'); load();
    } catch (e) { toast.error(parseError(e)); }
  };
  const sendActa = async (okrId) => {
    try {
      const res = await axios.post(`${API}/qa/okrs/${okrId}/acta/send`, {}, { headers: authHeaders() });
      try { await navigator.clipboard.writeText(res.data.signing_link); } catch {}
      const emailMsg = res.data.email_sent ? ` y email enviado a ${res.data.recipient_email}` : ' (email no enviado: cliente sin email o SMTP)';
      toast.success(`Link copiado al portapapeles${emailMsg}`);
      load();
    } catch (e) { toast.error(parseError(e)); }
  };
  const signActaAnalytica = async (okr, payload) => {
    // payload: { mode: 'simple', signature_data_url } | { mode: 'cert', p12_base64, password }
    try {
      if (payload.mode === 'simple') {
        await axios.post(`${API}/qa/okrs/${okr.id}/acta/sign-analytica`,
          { analytica_signature_data_url: payload.signature_data_url }, { headers: authHeaders() });
        toast.success('Acta firmada por Analytica');
      } else if (payload.mode === 'cert') {
        // Generamos el PDF actual del acta (con datos de la firma del cliente)
        const okrBls = backlogs.filter(b => b.okr_id === okr.id);
        const { generateActaPDF } = await import('../utils/actaPdf');
        const doc = await generateActaPDF(okr, {
          plan_number: plan.plan_number, name: plan.name,
          client_company_name: plan.client_company_name, contract_number: plan.contract_number,
        }, okrBls, { download: false });
        const dataUri = doc.output('datauristring');
        const pdfBase64 = dataUri.split(',', 2)[1];
        const r = await axios.post(`${API}/signatures/sign-as-analytica`, {
          p12_base64: payload.p12_base64,
          password: payload.password,
          target_type: 'acta',
          target_id: okr.id,
          pdf_base64: pdfBase64,
        }, { headers: authHeaders() });
        toast.success(`Acta firmada con certificado PAdES · ${r.data.cert_info?.common_name}`);
      }
      load();
    } catch (e) { toast.error(parseError(e)); }
  };
  const downloadActaPDF = async (okr) => {
    try {
      const okrBls = backlogs.filter(b => b.okr_id === okr.id);
      const { generateActaPDF } = await import('../utils/actaPdf');
      await generateActaPDF(okr, {
        plan_number: plan.plan_number, name: plan.name,
        client_company_name: plan.client_company_name, contract_number: plan.contract_number,
      }, okrBls, { download: true });
      toast.success('PDF descargado');
    } catch (e) { toast.error(parseError(e)); }
  };

  return (
    <div className="space-y-3" data-testid="estructura-tab">
      {isAdmin && (
        <div className="flex justify-between items-center">
          <p className="text-sm text-gray-600">Jerarquia: <strong>Hito → OKR → Backlog (con checklist)</strong></p>
          <Button size="sm" onClick={createMs} className="bg-[#2F3D8E]" data-testid="new-milestone-btn">+ Nuevo Hito</Button>
        </div>
      )}
      {milestones.length === 0 ? (
        <div className="text-center py-10 text-gray-400 bg-white rounded-xl border">No hay hitos aun. {isAdmin && 'Crea uno para empezar.'}</div>
      ) : milestones.map((ms, msIdx) => {
        const msOkrs = okrs.filter(o => o.milestone_id === ms.id);
        const isOpen = expandedMs[ms.id] !== false;
        const palette = milestonePalette(msIdx);
        // Move targets for backlogs of this milestone (all OKRs across all milestones, with hito context)
        const moveTargets = milestones.flatMap((m, mi) => okrs.filter(o => o.milestone_id === m.id).map(o => ({
          okr_id: o.id,
          label: `${m.full_code || m.code || `H${mi+1}`} · ${(o.full_code || o.code || '').split('-').slice(-1)[0]} · ${o.objective?.slice(0, 30) || ''}${(o.objective || '').length > 30 ? '…' : ''}`,
        })));
        return (
          <div key={ms.id} className={`bg-white rounded-xl border-2 ${palette.border} overflow-hidden`} data-testid={`milestone-${ms.code}`}>
            <div className={`h-1.5 ${palette.strip}`}></div>
            <div className="flex items-start justify-between px-4 py-3 bg-[#2F3D8E] text-white gap-2">
              <button className="flex-shrink-0 mt-1" onClick={() => setExpandedMs(p => ({ ...p, [ms.id]: !isOpen }))}>
                {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </button>
              <div className="flex-1 space-y-1">
                <div className="text-xs opacity-80 flex items-center gap-2">
                  <span>{ms.full_code || ms.code}</span>
                  <span>·</span>
                  <span className="text-blue-100">Vence:</span>
                  <InlineEdit value={ms.target_date} onSave={(v) => updateMs(ms.id, { target_date: v || null })} type="date"
                              placeholder="fecha" canEdit={isAdmin} className="bg-white text-gray-700" />
                </div>
                <div className="font-bold text-lg">
                  <InlineEdit value={ms.name} onSave={(v) => updateMs(ms.id, { name: v })}
                              canEdit={isAdmin} className="bg-white text-gray-700" />
                </div>
                <div className="text-xs opacity-90">
                  <InlineEdit value={ms.description} onSave={(v) => updateMs(ms.id, { description: v })} multiline
                              canEdit={isAdmin} placeholder="Click para describir el hito..." className="bg-white text-gray-700" />
                </div>
              </div>
              {isAdmin && (
                <div className="flex gap-1 flex-wrap flex-shrink-0">
                  <Button size="sm" variant="outline" onClick={() => createOkr(ms.id)} className="bg-white/10 text-white border-white/30 hover:bg-white hover:text-[#2F3D8E] h-7 px-2 text-xs">+ OKR</Button>
                  <Button size="sm" variant="outline" onClick={() => deleteItem('milestones', ms.id, ms.name)} className="bg-white/10 text-white border-white/30 hover:bg-red-600 h-7 px-2 text-xs">Eliminar</Button>
                </div>
              )}
            </div>
            {isOpen && (
              <div className="p-3 space-y-3">
                {/* Comments + Evidence del Milestone */}
                <div className="grid md:grid-cols-2 gap-3">
                  <CommentsThread entityType="milestones" entityId={ms.id} comments={ms.comments || []} user={user} canAdd={isAdmin} onChanged={load} />
                  <EvidenceGallery entityType="milestones" entityId={ms.id} evidence={ms.evidence || []} user={user} canAdd={isAdmin} onChanged={load} />
                </div>
                {msOkrs.length === 0 ? (
                  <div className="text-xs text-gray-400 py-2 italic">Aun sin OKRs.</div>
                ) : msOkrs.map(okr => {
                  const okrBls = backlogs.filter(b => b.okr_id === okr.id);
                  const open2 = expandedOkr[okr.id] !== false;
                  return (
                    <div key={okr.id} className="border rounded-lg overflow-hidden" data-testid={`okr-${okr.code}`}>
                      <div className="bg-blue-50 px-3 py-2 flex items-start justify-between gap-2">
                        <button className="flex-shrink-0 mt-1" onClick={() => setExpandedOkr(p => ({ ...p, [okr.id]: !open2 }))}>
                          {open2 ? <ChevronDown className="w-4 h-4 text-blue-700" /> : <ChevronRight className="w-4 h-4 text-blue-700" />}
                        </button>
                        <div className="flex-1 space-y-1">
                          <div className="text-xs text-blue-700 font-semibold">{okr.full_code || okr.code}</div>
                          <div className="font-medium">
                            <InlineEdit value={okr.objective} onSave={(v) => updateOkr(okr.id, { objective: v })} canEdit={isAdmin} />
                          </div>
                          <div className="text-xs text-gray-600">
                            <InlineEdit value={okr.description} onSave={(v) => updateOkr(okr.id, { description: v })} multiline
                                        placeholder="Click para describir el OKR..." canEdit={isAdmin} />
                          </div>
                          <div className="text-[10px] text-gray-600 flex items-center gap-2">
                            <span>Fecha:</span>
                            <InlineEdit value={okr.target_date} onSave={(v) => updateOkr(okr.id, { target_date: v || null })} type="date" placeholder="-" canEdit={isAdmin} />
                          </div>
                          <div className="text-[10px] uppercase">
                            Acta: <span className="font-bold text-[#2F3D8E]">{okr.acta_status}</span>
                          </div>
                        </div>
                        {(isAdmin || isClient) && (
                          <div className="flex gap-1 flex-wrap flex-shrink-0">
                            <Button size="sm" variant="outline" onClick={() => createBl(okr.id, 'programacion')} className="h-6 px-2 text-[10px]" data-testid={`new-bl-prog-${okr.code}`}>+ Backlog Prog</Button>
                            <Button size="sm" variant="outline" onClick={() => createBl(okr.id, 'usuario')} className="h-6 px-2 text-[10px] border-orange-300 text-orange-700" data-testid={`new-bl-user-${okr.code}`}>+ Backlog User</Button>
                            {isAdmin && okr.acta_status !== 'completed' && (
                              <Button size="sm" variant="outline" onClick={() => sendActa(okr.id)} className="border-purple-500 text-purple-600 hover:bg-purple-500 hover:text-white h-6 px-2 text-[10px]" data-testid={`send-acta-${okr.code}`}>
                                {okr.acta_status === 'sent' ? 'Reenviar' : 'Enviar Acta'}
                              </Button>
                            )}
                            {isAdmin && (
                              <Button size="sm" variant="outline" onClick={() => downloadActaPDF(okr)} data-testid={`preview-acta-${okr.code}`} className="border-[#4C8C3B] text-[#4C8C3B] hover:bg-[#4C8C3B] hover:text-white h-6 px-2 text-[10px]">
                                Vista previa PDF
                              </Button>
                            )}
                            {isAdmin && !['completed','analytica_signed'].includes(okr.acta_status) && !okr.acta_analytica_signed_at && (
                              <Button size="sm" onClick={() => setSigningActaOkr(okr)} className="bg-[#4C8C3B] hover:bg-[#3d7030] text-white h-6 px-2 text-[10px]" data-testid={`sign-analytica-${okr.code}`}>
                                Firmar Analytica
                              </Button>
                            )}
                            {isAdmin && ['client_signed','analytica_signed','completed','sent'].includes(okr.acta_status) && (
                              <Button size="sm" variant="outline" onClick={() => downloadActaPDF(okr)} data-testid={`download-acta-${okr.code}`} className="border-[#2F3D8E] text-[#2F3D8E] h-6 px-2 text-[10px]">PDF firmado</Button>
                            )}
                            {isAdmin && (
                              <Button size="sm" variant="outline" onClick={() => deleteItem('okrs', okr.id, okr.objective)} className="text-red-600 border-red-300 h-6 px-2 text-[10px]">x</Button>
                            )}
                          </div>
                        )}
                      </div>
                      {open2 && (
                        <div className="px-3 py-2 space-y-3">
                          {/* Key results editable */}
                          <div className="bg-blue-50/50 border border-blue-100 rounded p-2 text-xs">
                            <div className="font-semibold text-blue-700 mb-1">Key Results</div>
                            {(okr.key_results || []).map((kr, i) => (
                              <div key={i} className="flex items-center gap-1 my-1">
                                <span className="text-gray-400">•</span>
                                {isAdmin ? (
                                  <InlineEdit value={kr} onSave={(v) => {
                                    const arr = [...(okr.key_results || [])];
                                    if (v) arr[i] = v; else arr.splice(i, 1);
                                    return updateOkr(okr.id, { key_results: arr });
                                  }} className="flex-1" />
                                ) : <span>{kr}</span>}
                              </div>
                            ))}
                            {isAdmin && (
                              <button onClick={async () => {
                                const v = window.prompt('Nuevo Key Result:');
                                if (v) await updateOkr(okr.id, { key_results: [...(okr.key_results || []), v] });
                              }} className="text-[10px] text-[#2F3D8E] hover:underline">+ Agregar KR</button>
                            )}
                          </div>
                          {/* Comments + Evidence del OKR */}
                          <div className="grid md:grid-cols-2 gap-3">
                            <CommentsThread entityType="okrs" entityId={okr.id} comments={okr.comments || []} user={user} canAdd={isAdmin} onChanged={load} />
                            <EvidenceGallery entityType="okrs" entityId={okr.id} evidence={okr.evidence || []} user={user} canAdd={isAdmin} onChanged={load} />
                          </div>
                          {/* Backlogs */}
                          {okrBls.length === 0 ? (
                            <div className="text-xs text-gray-400 italic py-1">Sin backlogs aun.</div>
                          ) : okrBls.map(bl => {
                            const open3 = expandedBl[bl.id] === true;
                            const stCls = STATUS_COLORS[bl.status || 'pending'];
                            return (
                              <div key={bl.id} className={`border-2 ${palette.border} rounded-lg ${palette.bg}`} data-testid={`backlog-${bl.code}`}>
                                <div className="flex items-start gap-2 px-3 py-2 bg-white/70">
                                  <button onClick={() => setExpandedBl(p => ({ ...p, [bl.id]: !open3 }))} className="flex-shrink-0 mt-1">
                                    {open3 ? <ChevronDown className="w-4 h-4 text-gray-500" /> : <ChevronRight className="w-4 h-4 text-gray-500" />}
                                  </button>
                                  <div className="flex-1 space-y-1">
                                    <div className="flex items-center gap-2">
                                      <ActivityFlag backlog={bl} readAt={readMarkers[bl.id]} currentUsername={user?.sub || user?.username} onMarkRead={markRead} compact />
                                      <span className={`text-xs font-mono font-semibold ${palette.label}`}>{bl.full_code || bl.code}</span>
                                      <span className={`text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded font-semibold ${(bl.test_type || 'programacion') === 'usuario' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'}`} data-testid={`bl-type-badge-${bl.code}`}>
                                        {(bl.test_type || 'programacion') === 'usuario' ? 'Usuario' : 'Programacion'}
                                      </span>
                                      <div className="font-medium flex-1">
                                        <InlineEdit value={bl.title} onSave={(v) => updateBl(bl.id, { title: v })} canEdit={canEditBacklog} />
                                      </div>
                                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${stCls.cls}`}>{stCls.label}</span>
                                    </div>
                                    <div className="text-xs text-gray-500 flex items-center gap-3 flex-wrap">
                                      <span>Prio:
                                        {canEditBacklog ? (
                                          <select value={bl.priority || 'medium'} onChange={e => updateBl(bl.id, { priority: e.target.value })}
                                                  className="ml-1 text-xs bg-transparent border rounded px-1">
                                            <option value="high">Alta</option><option value="medium">Media</option><option value="low">Baja</option>
                                          </select>
                                        ) : <strong className="ml-1">{PRIORITY_LABEL[bl.priority || 'medium']}</strong>}
                                      </span>
                                      <span>Estado:
                                        {canEditBacklog ? (
                                          <select value={bl.status || 'pending'} onChange={e => updateBl(bl.id, { status: e.target.value })}
                                                  className="ml-1 text-xs bg-transparent border rounded px-1" data-testid={`bl-status-${bl.code}`}>
                                            <option value="pending">Pendiente</option>
                                            <option value="in_progress">En curso</option>
                                            <option value="passed">Aprobado</option>
                                            <option value="failed">Fallido</option>
                                            <option value="blocked">Bloqueado</option>
                                          </select>
                                        ) : <strong className="ml-1">{stCls.label}</strong>}
                                      </span>
                                      <span className="inline-flex items-center gap-1">Asignado:
                                        {canEditBacklog ? (
                                          <AssigneeSelect value={bl.assigned_to} onChange={(v) => updateBl(bl.id, { assigned_to: v })}
                                                          users={assignableUsers} compact testId={`bl-assignee-${bl.code}`} />
                                        ) : <strong className="ml-1">{bl.assigned_to || '-'}</strong>}
                                      </span>
                                      <span>Vence: <InlineEdit value={bl.due_date} onSave={(v) => updateBl(bl.id, { due_date: v || null })} type="date" placeholder="-" canEdit={canEditBacklog} /></span>
                                      <span>Tipo:
                                        {canEditBacklog ? (
                                          <select value={bl.test_type || 'programacion'} onChange={e => updateBl(bl.id, { test_type: e.target.value })}
                                                  className="ml-1 text-xs bg-transparent border rounded px-1" data-testid={`bl-type-${bl.code}`}>
                                            <option value="programacion">Programacion</option>
                                            <option value="usuario">Usuario</option>
                                          </select>
                                        ) : <strong className="ml-1">{(bl.test_type || 'programacion') === 'usuario' ? 'Usuario' : 'Programacion'}</strong>}
                                      </span>
                                      {canEditBacklog && moveTargets.length > 1 && (
                                        <span className="inline-flex items-center gap-1">Mover a:
                                          <select value={bl.okr_id || ''}
                                                  onChange={e => {
                                                    const newOkr = e.target.value;
                                                    if (newOkr && newOkr !== bl.okr_id && window.confirm('¿Mover este backlog al OKR seleccionado?')) {
                                                      updateBl(bl.id, { okr_id: newOkr });
                                                    }
                                                  }}
                                                  className="ml-1 text-xs bg-white border rounded px-1 max-w-[220px]"
                                                  data-testid={`bl-move-${bl.code}`}>
                                            {moveTargets.map(t => (
                                              <option key={t.okr_id} value={t.okr_id}>{t.label}</option>
                                            ))}
                                          </select>
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  {canEditBacklog && (() => {
                                    // Clients can only delete backlogs they created themselves
                                    const canDelete = isAdmin || (isClient && bl.created_by === user?.sub && bl.created_by_role === 'client_qa');
                                    return canDelete ? (
                                      <Button size="sm" variant="outline" onClick={() => deleteItem('backlogs', bl.id, bl.title)} className="text-red-600 border-red-300 h-6 px-2 text-[10px] flex-shrink-0">x</Button>
                                    ) : null;
                                  })()}
                                </div>
                                {open3 && (
                                  <div className="px-4 py-3 bg-gray-50/50 space-y-3">
                                    <div>
                                      <Label className="text-xs">Descripcion</Label>
                                      <InlineEdit value={bl.description} onSave={(v) => updateBl(bl.id, { description: v })} multiline
                                                  placeholder="Click para describir..." canEdit={canEditBacklog}
                                                  displayClassName="block text-xs text-gray-700 mt-1" />
                                    </div>
                                    <div>
                                      <Label className="text-xs">Requisito / Trazabilidad</Label>
                                      <InlineEdit value={bl.requirement} onSave={(v) => updateBl(bl.id, { requirement: v })}
                                                  placeholder="RF-001: ..." canEdit={canEditBacklog}
                                                  displayClassName="block text-xs text-gray-700 mt-1" />
                                    </div>
                                    <div>
                                      <Label className="text-xs">Ultima observacion</Label>
                                      <InlineEdit value={bl.last_note} onSave={(v) => updateBl(bl.id, { last_note: v })} multiline
                                                  placeholder="Notas de ejecucion..." canEdit={canEditBacklog}
                                                  displayClassName="block text-xs text-gray-700 mt-1" />
                                    </div>
                                    <Checklist backlogId={bl.id} checklist={bl.checklist || []} user={user} isClient={isClient} canManage={canEditBacklog} clientCompanyName={plan.client_company_name} assignableUsers={assignableUsers} itemMarkers={itemMarkers} onMarkItemRead={markRead} moveBacklogTargets={backlogs.filter(b2 => b2.id !== bl.id).map(b2 => ({ id: b2.id, label: `${b2.full_code || b2.code} · ${(b2.title || '').slice(0, 30)}${(b2.title || '').length > 30 ? '…' : ''}` }))} onChanged={load} />
                                    <div className="grid md:grid-cols-2 gap-3">
                                      <CommentsThread entityType="backlogs" entityId={bl.id} comments={bl.comments || []} user={user} canAdd={isAdmin} onChanged={load} />
                                      <EvidenceGallery entityType="backlogs" entityId={bl.id} evidence={bl.evidence || []} user={user} canAdd={isAdmin} onChanged={load} />
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {signingActaOkr && (
        <AnalyticaSignatureModal okr={signingActaOkr}
          onClose={() => setSigningActaOkr(null)}
          onSigned={(payload) => { signActaAnalytica(signingActaOkr, payload); setSigningActaOkr(null); }} />
      )}
    </div>
  );
};

// ============================================================================
// ANALYTICA SIGNATURE MODAL (3 modos: dibujar / imagen / PAdES)
// ============================================================================
const AnalyticaSignatureModal = ({ okr, onClose, onSigned }) => {
  const canvasRef = useRef(null);
  const fileRef = useRef(null);
  const [mode, setMode] = useState('draw'); // 'draw' | 'upload' | 'cert'
  const [drawing, setDrawing] = useState(false);
  const [hasSig, setHasSig] = useState(false);
  const [uploadedSig, setUploadedSig] = useState(null);
  const [certData, setCertData] = useState(null);
  const [busy, setBusy] = useState(false);
  const authToken = typeof window !== 'undefined' ? localStorage.getItem('token') : null;

  useEffect(() => {
    if (mode !== 'draw') return;
    const c = canvasRef.current; if (!c) return;
    const ctx = c.getContext('2d');
    ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, c.width, c.height);
    ctx.strokeStyle = '#2F3D8E'; ctx.lineWidth = 2; ctx.lineCap = 'round';
    setHasSig(false);
  }, [mode]);

  const start = (e) => {
    setDrawing(true);
    const c = canvasRef.current; const ctx = c.getContext('2d');
    const rect = c.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.beginPath(); ctx.moveTo(x, y);
  };
  const move = (e) => {
    if (!drawing) return;
    const c = canvasRef.current; const ctx = c.getContext('2d');
    const rect = c.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.lineTo(x, y); ctx.stroke(); setHasSig(true);
  };
  const stop = () => setDrawing(false);
  const clearC = () => {
    if (mode === 'draw') {
      const c = canvasRef.current; if (!c) return;
      const ctx = c.getContext('2d');
      ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, c.width, c.height);
    } else if (mode === 'upload') {
      setUploadedSig(null);
    }
    setHasSig(false);
  };

  const handleFileUpload = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith('image/')) { toast.error('Imagen invalida'); return; }
    if (f.size > 5 * 1024 * 1024) { toast.error('Maximo 5MB'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => { setUploadedSig(ev.target.result); setHasSig(true); };
    reader.readAsDataURL(f);
  };

  const submit = async () => {
    setBusy(true);
    try {
      if (mode === 'draw') {
        if (!hasSig) { toast.error('Dibuje su firma'); setBusy(false); return; }
        onSigned({ mode: 'simple', signature_data_url: canvasRef.current.toDataURL('image/png') });
      } else if (mode === 'upload') {
        if (!uploadedSig) { toast.error('Suba la imagen de su firma'); setBusy(false); return; }
        onSigned({ mode: 'simple', signature_data_url: uploadedSig });
      } else if (mode === 'cert') {
        if (!certData) { toast.error('Cargue y valide su certificado primero'); setBusy(false); return; }
        onSigned({ mode: 'cert', p12_base64: certData.p12_base64, password: certData.password });
      }
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4 overflow-y-auto" data-testid="analytica-sign-modal">
      <div className="bg-white rounded-xl w-full max-w-2xl my-8">
        <div className="p-4 border-b flex items-center justify-between">
          <h3 className="font-bold text-[#1a2456]">Firmar como Analytica · {okr.code}</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-gray-400" /></button>
        </div>
        <div className="p-4">
          <p className="text-xs text-gray-600 mb-3">El cliente ya firmo. Elija el metodo de firma para Analytica:</p>

          {/* Tab selector */}
          <div className="flex gap-1 mb-4 border-b">
            <button onClick={() => setMode('draw')}
                    className={`px-3 py-2 text-xs font-medium border-b-2 -mb-px ${mode === 'draw' ? 'border-[#2F3D8E] text-[#2F3D8E]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                    data-testid="analytica-mode-draw">
              Dibujar
            </button>
            <button onClick={() => setMode('upload')}
                    className={`px-3 py-2 text-xs font-medium border-b-2 -mb-px ${mode === 'upload' ? 'border-[#2F3D8E] text-[#2F3D8E]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                    data-testid="analytica-mode-upload">
              Subir Imagen
            </button>
            <button onClick={() => setMode('cert')}
                    className={`px-3 py-2 text-xs font-medium border-b-2 -mb-px ${mode === 'cert' ? 'border-[#2F3D8E] text-[#2F3D8E]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                    data-testid="analytica-mode-cert">
              Firma con Certificado <span className="ml-1 text-[10px] bg-[#4C8C3B] text-white px-1.5 py-0.5 rounded-full">PAdES</span>
            </button>
          </div>

          {mode === 'draw' && (
            <div className="border-2 border-dashed border-gray-300 rounded-xl p-2 mb-3">
              <canvas ref={canvasRef} width={500} height={180}
                      className="w-full bg-white rounded cursor-crosshair touch-none"
                      onMouseDown={start} onMouseMove={move} onMouseUp={stop} onMouseLeave={stop}
                      onTouchStart={start} onTouchMove={move} onTouchEnd={stop} />
            </div>
          )}

          {mode === 'upload' && (
            <>
              <input type="file" ref={fileRef} onChange={handleFileUpload} accept="image/*" className="hidden" data-testid="analytica-upload-input" />
              <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 mb-3 text-center cursor-pointer hover:border-[#2F3D8E]"
                   onClick={() => fileRef.current?.click()}>
                {uploadedSig ? (
                  <img src={uploadedSig} alt="firma analytica" className="max-h-32 mx-auto rounded-lg border" />
                ) : (
                  <div className="space-y-2 text-gray-500">
                    <ImageIcon className="w-10 h-10 mx-auto" />
                    <p>Haga clic para seleccionar imagen de firma</p>
                    <p className="text-xs">PNG, JPG (max 5MB)</p>
                  </div>
                )}
              </div>
            </>
          )}

          {mode === 'cert' && (
            <CertSignaturePanel
              authToken={authToken}
              onReady={(p12_base64, password, info) => setCertData({ p12_base64, password, info })}
              onClear={() => setCertData(null)} />
          )}

          <div className="flex gap-2 justify-end mt-4">
            {(mode === 'draw' || mode === 'upload') && (
              <Button variant="outline" onClick={clearC} disabled={!hasSig}>Limpiar</Button>
            )}
            <Button variant="ghost" onClick={onClose}>Cancelar</Button>
            <Button onClick={submit} disabled={busy || (mode === 'cert' ? !certData : !hasSig)}
                    className="bg-[#4C8C3B] hover:bg-[#3d7030] text-white" data-testid="confirm-analytica-sign-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5"/> : null}
              Confirmar firma
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// MATRIZ TAB · 2-column Kanban (Programacion vs Usuario)
// Cliente puede agregar evidencias/comentarios y togglear checklist
// ============================================================================
const MatrizTab = ({ plan, user, isAdmin, isClient, onChanged }) => {
  const { users: assignableUsers } = useAssignableUsers(plan.id);
  const { markers: readMarkers, itemMarkers, markRead } = useReadMarkers(plan.id);
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [filterMs, setFilterMs] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/qa/plans/${plan.id}/matrix`, { headers: authHeaders() });
      setData(r.data);
      onChanged?.();
    } catch (e) { toast.error(parseError(e)); }
  }, [plan.id, onChanged]);

  useEffect(() => { load(); }, [load]);
  if (!data) return <div className="text-center py-20 text-gray-400">Cargando matriz...</div>;

  // Flatten backlogs from matrix with parent context
  const flat = [];
  for (const ms of data.milestones || []) {
    for (const okr of ms.okrs || []) {
      for (const bl of okr.backlogs || []) {
        flat.push({
          ...bl,
          _ms: { id: ms.id, code: ms.full_code || ms.code, name: ms.name },
          _okr: { id: okr.id, code: okr.full_code || okr.code, objective: okr.objective },
        });
      }
    }
  }

  // Build move targets (all OKRs across all milestones) + backlog list for task-move
  const milestonesWithIdx = (data.milestones || []).map((m, i) => ({ ...m, _idx: i }));
  const msColorIndex = {};
  milestonesWithIdx.forEach((m) => { msColorIndex[m.id] = m._idx; });
  const moveOkrTargets = [];
  for (const ms of milestonesWithIdx) {
    for (const okr of ms.okrs || []) {
      const msCode = ms.full_code || ms.code || `H${ms._idx + 1}`;
      const okrCode = (okr.full_code || okr.code || '').split('-').slice(-1)[0];
      moveOkrTargets.push({
        okr_id: okr.id,
        label: `${msCode} · ${okrCode} · ${(okr.objective || '').slice(0, 30)}${(okr.objective || '').length > 30 ? '…' : ''}`,
      });
    }
  }
  const allBacklogsForMove = flat.map(b => ({ id: b.id, label: `${b.full_code || b.code} · ${(b.title || '').slice(0, 30)}${(b.title || '').length > 30 ? '…' : ''}` }));

  // Apply filters
  const filtered = flat.filter(b => {
    if (filterMs !== 'all' && b._ms.id !== filterMs) return false;
    if (filterStatus !== 'all' && (b.status || 'pending') !== filterStatus) return false;
    return true;
  });
  const colProg = filtered.filter(b => (b.test_type || 'programacion') === 'programacion');
  const colUser = filtered.filter(b => (b.test_type || 'programacion') === 'usuario');

  const updateBlStatus = async (id, status) => {
    try {
      await axios.put(`${API}/qa/backlogs/${id}`, { status }, { headers: authHeaders() });
      load();
    } catch (e) { toast.error(parseError(e)); }
  };

  const acceptByClient = async (bl, accepted) => {
    try {
      await axios.post(`${API}/qa/backlogs/${bl.id}/accept`,
        { accepted, comment: '' }, { headers: authHeaders() });
      toast.success(accepted ? 'Marcado como aceptado' : 'Aceptacion revertida');
      load();
    } catch (e) { toast.error(parseError(e)); }
  };

  const sendBacklogSummary = async (bl) => {
    if (!window.confirm(`Enviar resumen del backlog ${bl.full_code || bl.code} a los destinatarios configurados?`)) return;
    try {
      const r = await axios.post(`${API}/qa/plans/${plan.id}/alerts/send-backlog-summary/${bl.id}`, {}, { headers: authHeaders() });
      toast.success(`Resumen enviado a ${r.data.recipients?.length || 0} destinatario(s)`);
    } catch (e) { toast.error(parseError(e)); }
  };

  const KanbanCard = ({ bl, accent }) => {
    const isOpen = !!expanded[bl.id];
    const stCls = STATUS_COLORS[bl.status || 'pending'];
    const cl = bl.checklist || [];
    const doneN = cl.filter(c => c.done).length;
    const evN = (bl.evidence || []).length + cl.reduce((s, c) => s + (c.evidence || []).length, 0);
    const coN = (bl.comments || []).length + cl.reduce((s, c) => s + (c.comments || []).length, 0);
    const palette = milestonePalette(msColorIndex[bl._ms.id] ?? 0);
    return (
      <div className={`bg-white rounded-lg border-l-4 ${accent} border border-gray-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden`}
           data-testid={`kanban-card-${bl.code}`}>
        <div className={`h-1 ${palette.strip}`} title={`Hito: ${bl._ms.name}`}></div>
        <div className="p-3 cursor-pointer" onClick={() => setExpanded(p => ({ ...p, [bl.id]: !isOpen }))}>
          <div className="flex items-start gap-2">
            <button className="flex-shrink-0 mt-0.5">
              {isOpen ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
            </button>
            <div className="flex-1 min-w-0">
              <div className={`text-[10px] font-mono truncate ${palette.label}`}>{bl._ms.code} · {bl._okr.code}</div>
              <div className="text-xs font-mono text-[#2F3D8E] font-semibold flex items-center gap-1.5">
                <ActivityFlag backlog={bl} readAt={readMarkers[bl.id]} currentUsername={user?.sub || user?.username} onMarkRead={markRead} compact />
                {bl.full_code || bl.code}
              </div>
              <div className="text-sm font-medium text-gray-800 mt-0.5 truncate">{bl.title}</div>
              <div className="text-[10px] text-gray-500 mt-0.5 truncate italic">↳ {bl._okr.objective}</div>
            </div>
            <span className={`text-[10px] px-2 py-0.5 rounded-full flex-shrink-0 ${stCls.cls}`}>{stCls.label}</span>
          </div>
          <div className="mt-2 flex items-center gap-2 text-[10px] text-gray-500 flex-wrap">
            {cl.length > 0 && (
              <span className="bg-gray-100 px-1.5 py-0.5 rounded inline-flex items-center gap-1">
                <ListChecks className="w-2.5 h-2.5" /> {doneN}/{cl.length}
              </span>
            )}
            {evN > 0 && <span className="bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded inline-flex items-center gap-1"><ImageIcon className="w-2.5 h-2.5" />{evN}</span>}
            {coN > 0 && <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded inline-flex items-center gap-1"><MessageSquare className="w-2.5 h-2.5" />{coN}</span>}
            {bl.priority && <span className={`px-1.5 py-0.5 rounded ${bl.priority === 'high' ? 'bg-red-50 text-red-600' : bl.priority === 'low' ? 'bg-gray-50 text-gray-500' : 'bg-amber-50 text-amber-700'}`}>{PRIORITY_LABEL[bl.priority]}</span>}
            {bl.due_date && <span className="text-gray-500">📅 {bl.due_date}</span>}
            {bl.assigned_to && <span className="text-gray-600">👤 {bl.assigned_to}</span>}
            {bl.client_accepted && <span className="bg-green-100 text-green-700 px-1.5 py-0.5 rounded">✓ Aceptado</span>}
          </div>
        </div>
        {isOpen && (
          <div className="border-t bg-gray-50/50 p-3 space-y-3">
            {bl.description && <p className="text-xs text-gray-600">{bl.description}</p>}
            {bl.requirement && <p className="text-xs text-gray-500"><strong>Requisito:</strong> {bl.requirement}</p>}
            {bl.last_note && <p className="text-xs italic text-gray-500">Obs: {bl.last_note}</p>}
            {(isAdmin || isClient) && (
              <div className="flex items-center gap-2 text-xs flex-wrap">
                <span className="text-gray-600">Estado:</span>
                <select value={bl.status || 'pending'} onChange={e => updateBlStatus(bl.id, e.target.value)}
                        className="text-xs border rounded px-1.5 py-0.5" data-testid={`kanban-status-${bl.code}`}>
                  <option value="pending">Pendiente</option>
                  <option value="in_progress">En curso</option>
                  <option value="passed">Aprobado</option>
                  <option value="failed">Fallido</option>
                  <option value="blocked">Bloqueado</option>
                </select>
                <span className="text-gray-600 ml-1">Asignado:</span>
                <AssigneeSelect value={bl.assigned_to}
                                onChange={async (v) => {
                                  try {
                                    await axios.put(`${API}/qa/backlogs/${bl.id}`, { assigned_to: v }, { headers: authHeaders() });
                                    toast.success(v ? 'Asignacion actualizada (se notificara por email)' : 'Asignacion removida');
                                    load();
                                  } catch (e) { toast.error(parseError(e)); }
                                }}
                                users={assignableUsers} compact testId={`kanban-assignee-${bl.code}`} />
                {isAdmin && (
                  <button onClick={() => sendBacklogSummary(bl)}
                          className="ml-auto text-[10px] inline-flex items-center gap-1 px-2 py-1 rounded bg-[#2F3D8E] text-white hover:bg-[#1f2d6e]"
                          data-testid={`send-backlog-summary-${bl.code}`}>
                    <Send className="w-3 h-3" /> Enviar resumen
                  </button>
                )}
              </div>
            )}
            {(isAdmin || isClient) && moveOkrTargets.length > 1 && (
              <div className="flex items-center gap-2 text-xs">
                <span className="text-gray-600">Mover backlog a OKR:</span>
                <select value={bl._okr.id}
                        onChange={async e => {
                          const newOkr = e.target.value;
                          if (newOkr && newOkr !== bl._okr.id && window.confirm('¿Mover este backlog al OKR seleccionado?')) {
                            try {
                              await axios.put(`${API}/qa/backlogs/${bl.id}`, { okr_id: newOkr }, { headers: authHeaders() });
                              toast.success('Backlog movido'); load();
                            } catch (err) { toast.error(parseError(err)); }
                          }
                        }}
                        className="text-xs border rounded px-1.5 py-0.5 max-w-[260px]"
                        data-testid={`kanban-move-${bl.code}`}>
                  {moveOkrTargets.map(t => (
                    <option key={t.okr_id} value={t.okr_id}>{t.label}</option>
                  ))}
                </select>
              </div>
            )}
            <Checklist
              backlogId={bl.id}
              checklist={cl}
              user={user}
              isClient={isClient}
              canManage={isAdmin || isClient}
              clientCompanyName={plan.client_company_name}
              assignableUsers={assignableUsers}
              itemMarkers={itemMarkers}
              onMarkItemRead={markRead}
              moveBacklogTargets={allBacklogsForMove.filter(b => b.id !== bl.id)}
              onChanged={load} />
            <div className="grid md:grid-cols-2 gap-3">
              <CommentsThread entityType="backlogs" entityId={bl.id} comments={bl.comments || []} user={user} canAdd={true} onChanged={load} />
              <EvidenceGallery entityType="backlogs" entityId={bl.id} evidence={bl.evidence || []} user={user} canAdd={true} onChanged={load} />
            </div>
            {isClient && (
              <div className="border-t pt-2 flex items-center justify-end gap-2">
                <Button size="sm" variant={bl.client_accepted ? 'outline' : 'default'}
                        className={bl.client_accepted ? 'border-amber-400 text-amber-600' : 'bg-[#4C8C3B] hover:bg-[#3d7030] text-white'}
                        onClick={() => acceptByClient(bl, !bl.client_accepted)}
                        data-testid={`client-accept-${bl.code}`}>
                  {bl.client_accepted ? 'Revertir aceptacion' : 'Marcar como aceptado'}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const StatBar = ({ stats }) => (
    <div className="flex items-center gap-1 text-[10px] flex-wrap">
      <span className="px-1.5 py-0.5 bg-green-100 text-green-700 rounded">{stats.passed} OK</span>
      <span className="px-1.5 py-0.5 bg-red-100 text-red-700 rounded">{stats.failed} F</span>
      <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded">{stats.pending} P</span>
      <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">{stats.in_progress} W</span>
      <span className="ml-1 font-semibold text-gray-700">{stats.pass_pct}% pass · {stats.coverage_pct}% cov</span>
    </div>
  );

  return (
    <div className="space-y-3" data-testid="matriz-tab">
      <div className="bg-gradient-to-r from-[#2F3D8E] to-[#5DA5D8] rounded-xl p-4 text-white">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-wide opacity-80">Matriz Kanban · {plan.client_company_name}</div>
            <div className="text-3xl font-bold">{data.global_stats.pass_pct}% pass rate</div>
            <StatBar stats={data.global_stats} />
          </div>
          <div className="text-right text-xs space-y-1">
            <div>Total backlogs: <strong>{flat.length}</strong></div>
            <div>Programacion: <strong>{colProg.length}</strong> · Usuario: <strong>{colUser.length}</strong></div>
          </div>
        </div>
      </div>

      {flat.length === 0 ? (
        <div className="text-center py-10 text-gray-400 bg-white rounded-xl border">Aun no hay estructura. Crea Hitos/OKR/Backlog en la pestana "Estructura".</div>
      ) : (
        <>
          {/* Filters */}
          <div className="bg-white border rounded-xl p-3 flex items-center gap-3 flex-wrap text-xs">
            <span className="text-gray-600 font-semibold">Filtros:</span>
            <select value={filterMs} onChange={e => setFilterMs(e.target.value)}
                    className="border rounded px-2 py-1" data-testid="matriz-filter-ms">
              <option value="all">Todos los hitos</option>
              {(data.milestones || []).map(ms => (
                <option key={ms.id} value={ms.id}>{ms.full_code || ms.code} · {ms.name}</option>
              ))}
            </select>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
                    className="border rounded px-2 py-1" data-testid="matriz-filter-status">
              <option value="all">Todos los estados</option>
              <option value="pending">Pendiente</option>
              <option value="in_progress">En curso</option>
              <option value="passed">Aprobado</option>
              <option value="failed">Fallido</option>
              <option value="blocked">Bloqueado</option>
            </select>
            {(filterMs !== 'all' || filterStatus !== 'all') && (
              <button onClick={() => { setFilterMs('all'); setFilterStatus('all'); }}
                      className="text-[#2F3D8E] hover:underline">Limpiar</button>
            )}
            <span className="ml-auto text-gray-500">Mostrando {filtered.length} de {flat.length}</span>
          </div>

          {/* Kanban Columns */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {/* Programacion */}
            <div className="bg-blue-50/40 rounded-xl border border-blue-200 p-3 min-h-[400px]" data-testid="kanban-col-programacion">
              <div className="flex items-center justify-between mb-3 pb-2 border-b border-blue-200">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                  <h3 className="font-bold text-blue-900">Pruebas de Programacion</h3>
                </div>
                <span className="text-xs bg-blue-200 text-blue-800 px-2 py-0.5 rounded-full font-semibold">{colProg.length}</span>
              </div>
              <div className="space-y-2">
                {colProg.length === 0 ? (
                  <div className="text-center py-12 text-blue-300 text-xs">Sin backlogs de programacion</div>
                ) : colProg.map(bl => <KanbanCard key={bl.id} bl={bl} accent="border-l-blue-500" />)}
              </div>
            </div>

            {/* Usuario */}
            <div className="bg-orange-50/40 rounded-xl border border-orange-200 p-3 min-h-[400px]" data-testid="kanban-col-usuario">
              <div className="flex items-center justify-between mb-3 pb-2 border-b border-orange-200">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                  <h3 className="font-bold text-orange-900">Pruebas de Usuario</h3>
                </div>
                <span className="text-xs bg-orange-200 text-orange-800 px-2 py-0.5 rounded-full font-semibold">{colUser.length}</span>
              </div>
              <div className="space-y-2">
                {colUser.length === 0 ? (
                  <div className="text-center py-12 text-orange-300 text-xs">Sin backlogs de usuario</div>
                ) : colUser.map(bl => <KanbanCard key={bl.id} bl={bl} accent="border-l-orange-500" />)}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ============================================================================
// CALENDARIO TAB (basado en backlogs)
// ============================================================================
const CalendarioTab = ({ plan }) => {
  const [data, setData] = useState(null);
  const [month, setMonth] = useState(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() }; });
  useEffect(() => {
    axios.get(`${API}/qa/plans/${plan.id}/calendar`, { headers: authHeaders() })
      .then(r => setData(r.data))
      .catch(e => toast.error(parseError(e)));
  }, [plan.id]);
  if (!data) return <div className="text-center py-20 text-gray-400">Cargando calendario...</div>;
  const first = new Date(month.y, month.m, 1);
  const last = new Date(month.y, month.m + 1, 0);
  const daysInMonth = last.getDate();
  const startWeekday = first.getDay();
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const iso = `${month.y}-${String(month.m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    cells.push({ iso, day: d, items: data.by_date[iso] || [] });
  }
  const monthName = new Date(month.y, month.m).toLocaleDateString('es-EC', { month: 'long', year: 'numeric' });
  const pieData = [
    { name: 'Completados', value: data.pie_today.completed, color: '#4C8C3B' },
    { name: 'Restantes', value: data.pie_today.remaining, color: '#cccccc' },
  ];
  const today = new Date().toISOString().slice(0, 10);
  const bars = data.daily.filter(d => d.date <= today).slice(-14);
  return (
    <div className="space-y-4" data-testid="calendario-tab">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border p-4 lg:col-span-2">
          <h3 className="font-semibold mb-2 text-gray-700">Backlogs por fecha (ultimos 14 dias)</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={bars}>
              <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Legend />
              <Bar dataKey="passed" stackId="a" fill="#4C8C3B" name="Aprobados" />
              <Bar dataKey="failed" stackId="a" fill="#e11d48" name="Fallidos" />
              <Bar dataKey="in_progress" stackId="a" fill="#5DA5D8" name="En progreso" />
              <Bar dataKey="pending" stackId="a" fill="#f59e0b" name="Pendientes" />
              <Bar dataKey="overdue" stackId="a" fill="#9333ea" name="Atrasados" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white rounded-xl border p-4">
          <h3 className="font-semibold mb-2 text-gray-700">Hoy: completados vs restantes</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} label={(d) => `${d.name}: ${d.value}`}>
                {pieData.map((e, i) => <Cell key={i} fill={e.color} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="text-center text-2xl font-bold text-[#4C8C3B]">{data.pie_today.completed_pct}%</div>
          <div className="text-center text-xs text-gray-500">completado</div>
        </div>
      </div>
      <div className="bg-white rounded-xl border p-4">
        <div className="flex items-center justify-between mb-3">
          <Button size="sm" variant="outline" onClick={() => setMonth(p => ({ y: p.m === 0 ? p.y - 1 : p.y, m: p.m === 0 ? 11 : p.m - 1 }))}>‹</Button>
          <h3 className="font-bold text-lg capitalize">{monthName}</h3>
          <Button size="sm" variant="outline" onClick={() => setMonth(p => ({ y: p.m === 11 ? p.y + 1 : p.y, m: p.m === 11 ? 0 : p.m + 1 }))}>›</Button>
        </div>
        <div className="grid grid-cols-7 gap-1 text-xs font-semibold text-gray-500 mb-1">
          {['Dom','Lun','Mar','Mie','Jue','Vie','Sab'].map(d => <div key={d} className="text-center py-1">{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((c, i) => (
            <div key={i} className={`min-h-[70px] border rounded-md p-1 ${c ? 'bg-white' : 'bg-gray-50'}`}>
              {c && (
                <>
                  <div className="text-xs font-bold text-gray-500">{c.day}</div>
                  <div className="space-y-0.5 mt-1">
                    {c.items.slice(0, 3).map(it => (
                      <div key={it.id} className={`text-[9px] truncate px-1 rounded ${
                        it.status === 'passed' ? 'bg-green-100 text-green-800' :
                        it.status === 'failed' ? 'bg-red-100 text-red-800' :
                        it.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                        it.status === 'blocked' ? 'bg-gray-200 text-gray-700' :
                        'bg-amber-100 text-amber-800'
                      }`} title={`${it.code} ${it.title}`}>
                        {it.code} {it.title}
                      </div>
                    ))}
                    {c.items.length > 3 && <div className="text-[9px] text-gray-500">+{c.items.length - 3} más</div>}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default QAPage;
